import React, { useState, useEffect } from 'react';
import { useWorkspaceState } from '@/hooks/useWorkspaceState';
import { Search, BookOpen, ExternalLink, ChevronRight, FileText, Scale, Book, Newspaper, DollarSign, Lightbulb, Calendar, Copy, Check, Type, Calculator, Trash2, X, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { resourceApi, userApi } from '@/services/api';
import { courtPrecedents } from '@/data/courtPrecedents';
import { caseDetailsMap } from '@/data/caseDetails';
import { CaseDisplay } from './CaseDisplay';
import { useResourcesFontSize, RESOURCES_FONT_SIZE_OPTIONS } from '@/hooks/useResourcesFontSize';

interface ResourcesPageProps {
  onDocumentSelect?: (doc: any) => void;
}

type ResourceType = 'mpep' | '37cfr' | '35usc' | 'fedreg' | 'fees' | 'abstract';

interface ResourceConfig {
  id: ResourceType;
  label: string;
  icon: any;
  color: string;
  bgColor: string;
  borderColor: string;
  title: string;
  subtitle: string;
}

const resourceConfigs: Record<ResourceType, ResourceConfig> = {
  mpep: {
    id: 'mpep',
    label: 'MPEP',
    icon: BookOpen,
    color: 'blue',
    bgColor: 'bg-blue-600',
    borderColor: 'border-blue-600',
    title: 'Manual of Patent Examining Procedure',
    subtitle: 'USPTO Examination Guidelines',
  },
  '37cfr': {
    id: '37cfr',
    label: '37 CFR',
    icon: FileText,
    color: 'purple',
    bgColor: 'bg-purple-600',
    borderColor: 'border-purple-600',
    title: '37 C.F.R. - Code of Federal Regulations',
    subtitle: 'Rules of Practice in Patent Cases',
  },
  '35usc': {
    id: '35usc',
    label: '35 USC',
    icon: Scale,
    color: 'teal',
    bgColor: 'bg-teal-600',
    borderColor: 'border-teal-600',
    title: '35 U.S.C. - United States Code',
    subtitle: 'Federal Patent Statutes',
  },
  fedreg: {
    id: 'fedreg',
    label: 'Federal Register',
    icon: Newspaper,
    color: 'orange',
    bgColor: 'bg-orange-600',
    borderColor: 'border-orange-600',
    title: 'Federal Register',
    subtitle: 'USPTO Notices and Rules',
  },
  fees: {
    id: 'fees',
    label: 'USPTO Fees',
    icon: DollarSign,
    color: 'green',
    bgColor: 'bg-green-600',
    borderColor: 'border-green-600',
    title: 'USPTO Fee Schedule',
    subtitle: 'Current Patent Filing and Maintenance Fees',
  },
  abstract: {
    id: 'abstract',
    label: '101 Abstract Idea',
    icon: Lightbulb,
    color: 'amber',
    bgColor: 'bg-amber-600',
    borderColor: 'border-amber-600',
    title: '101 Abstract Idea Guidance',
    subtitle: 'Subject Matter Eligibility Guidelines',
  },
};

export function ResourcesPage({ onDocumentSelect }: ResourcesPageProps) {
  const [activeResource, setActiveResource] = useWorkspaceState<ResourceType>('resources:selected', 'mpep', value => typeof value === 'string' && !!resourceConfigs[value]);
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [searchQuery, setSearchQuery] = useWorkspaceState('resources:search', '');
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [lastSearchQuery, setLastSearchQuery] = useState<string>(''); // Track the query that produced current results
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchMode, setSearchMode] = useWorkspaceState<'section' | 'fulltext'>('resources:search-mode', 'section', value => value === 'section' || value === 'fulltext');
  const [accountInfo, setAccountInfo] = useState<any>(null);

  // Data states
  const [feeInfo, setFeeInfo] = useState<any>(null);
  const [uscInfo, setUscInfo] = useState<any>(null);
  const [fedRegInfo, setFedRegInfo] = useState<any>(null);
  const [mpepInfo, setMpepInfo] = useState<any>(null);
  const [sectionContent, setSectionContent] = useState<any>(null);

  // Font size settings
  const { resourcesFontSize, setResourcesFontSize, getFontSizeStyle } = useResourcesFontSize();

  const config = resourceConfigs[activeResource];
  const IconComponent = config.icon;

  // Check if user has pro/premium access
  const isPaidUser = accountInfo?.account_type === 'pro' || accountInfo?.account_type === 'premium';

  // Fetch account info on mount
  useEffect(() => {
    const fetchAccountInfo = async () => {
      try {
        const response = await userApi.fetchAccountInfo();
        if (response) {
          setAccountInfo(response);
        }
      } catch (error) {
        console.error('Error fetching account info:', error);
      }
    };
    fetchAccountInfo();
  }, []);

  // Ensure free users can't use full text search
  useEffect(() => {
    if (accountInfo && !isPaidUser && searchMode === 'fulltext') {
      setSearchMode('section');
    }
  }, [accountInfo, isPaidUser, searchMode]);

  // Auto-switch to Full Text mode when first character is a letter (for paid users only)
  // Auto-switch back to Section mode when input is cleared
  useEffect(() => {
    const trimmedQuery = searchQuery.trim();
    const firstChar = trimmedQuery.charAt(0);

    // Only auto-switch for MPEP and CFR (resources that support both modes)
    const supportsToggle = ['mpep', '37cfr'].includes(activeResource);

    if (supportsToggle && isPaidUser) {
      // Switch to fulltext when first character is a letter
      if (
        trimmedQuery.length > 0 &&
        /^[a-zA-Z]/.test(firstChar) &&
        searchMode === 'section'
      ) {
        setSearchMode('fulltext');
      }
      // Switch back to section when input is cleared
      else if (trimmedQuery.length === 0 && searchMode === 'fulltext') {
        setSearchMode('section');
      }
    }
  }, [searchQuery, activeResource, isPaidUser, searchMode]);

  // Fetch resource data when resource type changes
  useEffect(() => {
    const fetchResourceData = async () => {
      setLoading(true);
      try {
        switch (activeResource) {
          case 'fees':
            if (!feeInfo) {
              const data = await resourceApi.fetchFeeInfo();
              if (data) {
                // Extract fee_info array from response
                const fees = data.fee_info || data;
                setFeeInfo(fees);
              }
            }
            break;
          case '35usc':
            if (!uscInfo) {
              const data = await resourceApi.fetchUscInfo();
              if (data) setUscInfo(data);
            }
            break;
          case 'fedreg':
            if (!fedRegInfo) {
              const data = await resourceApi.fetchFedRegInfo();
              if (data) setFedRegInfo(data);
            }
            break;
          case 'mpep':
            if (!mpepInfo) {
              const data = await resourceApi.fetchMpepInfo();
              if (data) setMpepInfo(data);
            }
            break;
        }
      } catch (error) {
        console.error('Failed to fetch resource data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResourceData();
  }, [activeResource, feeInfo, uscInfo, fedRegInfo, mpepInfo]);

  // Fetch section content when section is selected
  useEffect(() => {
    const fetchSectionData = async () => {
      if (!selectedSection) {
        setSectionContent(null);
        return;
      }

      setLoading(true);
      try {
        if (selectedSection.startsWith('mpep-')) {
          const section = selectedSection.replace('mpep-', '');
          const data = await resourceApi.fetchMpepContent(section);
          if (data) setSectionContent(data);
        } else if (selectedSection.startsWith('cfr-')) {
          const section = selectedSection.replace('cfr-', '');
          const data = await resourceApi.fetchCfrContent(section);
          if (data) setSectionContent(data);
        } else if (selectedSection.startsWith('usc-')) {
          // USC content is fetched from uscInfo, not from individual section API
          // Just mark loading as complete
          setSectionContent(null);
        }
      } catch (error) {
        console.error('Failed to fetch section content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSectionData();
  }, [selectedSection]);

  const handleResourceChange = (resource: ResourceType) => {
    setActiveResource(resource);
    setSelectedSection(''); // Reset section when changing resources
    setSectionContent(null); // Reset section content
    setShowSearchResults(false); // Clear search results
    setSearchQuery(''); // Clear search query
    setSearchResults([]); // Clear results
  };

  const handleSectionSelect = (section: string) => {
    setSelectedSection(section);
  };

  const handleSearch = async () => {
    const query = searchQuery.trim();
    if (!query) return;

    // USC always does section navigation
    if (activeResource === '35usc') {
      setSelectedSection(`usc-${query}`);
      setSearchQuery('');
      setShowSearchResults(false);
      return;
    }

    // For Fed Reg, always do full-text search
    // For MPEP, CFR - respect the search mode toggle
    const shouldDoSectionNavigation =
      searchMode === 'section' && ['mpep', '37cfr'].includes(activeResource);

    if (shouldDoSectionNavigation) {
      // Navigate directly to section
      switch (activeResource) {
        case 'mpep':
          setSelectedSection(`mpep-${query}`);
          setSearchQuery('');
          setShowSearchResults(false);
          return;
        case '37cfr':
          setSelectedSection(`cfr-${query}`);
          setSearchQuery('');
          setShowSearchResults(false);
          return;
      }
    }

    // Perform full-text search
    setIsSearching(true);
    setShowSearchResults(true);

    try {
      let results = null;

      switch (activeResource) {
        case 'mpep':
          results = await resourceApi.performMpepQuery(query);
          setSearchResults(results?.mpep_query || []);
          setLastSearchQuery(query); // Lock in the query that produced these results
          break;
        case '37cfr':
          results = await resourceApi.performCfrQuery(query);
          setSearchResults(results?.cfr_query || []);
          setLastSearchQuery(query); // Lock in the query that produced these results
          break;
        case 'fedreg':
          results = await resourceApi.performFedRegQuery(query);
          setSearchResults(results?.fed_reg_query || []);
          setLastSearchQuery(query); // Lock in the query that produced these results
          break;
        default:
          setSearchResults([]);
          break;
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="flex h-full bg-white">
      {/* Left Sidebar - Resource Navigation */}
      <div className="w-64 bg-slate-900 flex flex-col border-r border-slate-700">
        <div className={`p-6 border-b border-slate-700 ${config.bgColor}`}>
          <div className="flex items-center gap-3 mb-2">
            <IconComponent className="w-6 h-6 text-white" />
            <h2 className="text-xl font-semibold text-white">{config.label}</h2>
          </div>
          <div className="text-sm text-white/80">{config.subtitle}</div>
        </div>

        <div className="flex-1 overflow-y-auto resource-nav-scroll bg-slate-900">
          <div className="p-4">
            {activeResource === 'mpep' && <MpepNavigation selectedSection={selectedSection} onSectionSelect={handleSectionSelect} mpepInfo={mpepInfo} />}
            {activeResource === '37cfr' && <CfrNavigation selectedSection={selectedSection} onSectionSelect={handleSectionSelect} />}
            {activeResource === '35usc' && <UscNavigation selectedSection={selectedSection} onSectionSelect={handleSectionSelect} />}
            {activeResource === 'fedreg' && <FedRegNavigation selectedSection={selectedSection} onSectionSelect={handleSectionSelect} fedRegInfo={fedRegInfo} />}
            {activeResource === 'fees' && <FeesNavigation selectedSection={selectedSection} onSectionSelect={handleSectionSelect} feeInfo={feeInfo} />}
            {activeResource === 'abstract' && <AbstractNavigation selectedSection={selectedSection} onSectionSelect={handleSectionSelect} />}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Navigation Bar */}
        <div className="border-b border-gray-200 bg-gray-50 pl-3 pr-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {(Object.keys(resourceConfigs) as ResourceType[]).map((key) => {
              const tabConfig = resourceConfigs[key];
              const TabIcon = tabConfig.icon;
              return (
                <Button
                  key={key}
                  variant={activeResource === key ? 'default' : 'ghost'}
                  size="sm"
                  className={`h-8 gap-1.5 ${
                    activeResource === key
                      ? `${tabConfig.bgColor} text-white hover:opacity-90`
                      : 'text-gray-700'
                  }`}
                  onClick={() => handleResourceChange(key)}
                >
                  <TabIcon className="w-3.5 h-3.5" />
                  {tabConfig.label}
                </Button>
              );
            })}
          </div>

          {/* Search Controls - Grouped Together */}
          <div className="inline-flex items-center gap-1.5 bg-white border border-gray-300 rounded-lg shadow-md overflow-hidden">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={
                  activeResource === 'mpep' && searchMode === 'section' ? 'Go to section (e.g., 2141)...' :
                  activeResource === 'mpep' && searchMode === 'fulltext' ? 'Search MPEP text...' :
                  activeResource === '37cfr' && searchMode === 'section' ? 'Go to section (e.g., 1.53)...' :
                  activeResource === '37cfr' && searchMode === 'fulltext' ? 'Search CFR text...' :
                  activeResource === '35usc' ? 'Go to section (e.g., 101)...' :
                  activeResource === 'fedreg' ? 'Search Federal Register...' :
                  'Search not available'
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="pl-9 w-72 h-9 border-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none"
                disabled={!['mpep', '37cfr', '35usc', 'fedreg'].includes(activeResource)}
              />
            </div>

            {/* Search Mode Toggle - Only show for MPEP, CFR */}
            {['mpep', '37cfr'].includes(activeResource) && (
              <>
                <div className="h-6 w-px bg-gray-300"></div>
                <div className="flex items-center gap-0.5 px-1">
                  <button
                    onClick={() => setSearchMode('section')}
                    className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${
                      searchMode === 'section'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    Section
                  </button>
                  <button
                    onClick={() => {
                      if (isPaidUser) {
                        setSearchMode('fulltext');
                      }
                    }}
                    disabled={!isPaidUser}
                    className={`px-3 py-1.5 text-xs font-medium rounded transition-all flex items-center gap-1 ${
                      searchMode === 'fulltext'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : !isPaidUser
                        ? 'text-gray-400 cursor-not-allowed opacity-60'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                    title={!isPaidUser ? 'Full text search is available for Pro and Premium users' : ''}
                  >
                    Text
                    {!isPaidUser && <Lock className="w-3 h-3" />}
                  </button>
                </div>
              </>
            )}

            <div className="h-6 w-px bg-gray-300"></div>
            <Button
              variant="default"
              size="sm"
              className={`h-9 w-9 p-0 ${config.bgColor} hover:opacity-90 rounded-none rounded-r-lg border-0 shadow-none`}
              onClick={handleSearch}
              disabled={!['mpep', '37cfr', '35usc', 'fedreg'].includes(activeResource) || !searchQuery.trim() || isSearching}
              title={isSearching ? 'Searching...' : searchMode === 'section' && activeResource !== 'fedreg' ? 'Go to section' : 'Search'}
            >
              <Search className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Search Results */}
        {showSearchResults && (
          <div className="border-b border-gray-200 bg-white">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-900">
                    Search Results for "{lastSearchQuery}"
                  </span>
                  <Badge variant="secondary" className="text-xs">
                    {searchResults.length} {searchResults.length === 1 ? 'result' : 'results'}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowSearchResults(false);
                    setSearchResults([]);
                    setSearchQuery('');
                  }}
                  className="h-7 text-xs"
                >
                  Clear Results
                </Button>
              </div>

              {isSearching ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full mx-auto mb-2"></div>
                  <div className="text-sm">Searching...</div>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <div className="text-sm">No results found</div>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {searchResults.map((result, idx) => (
                    <SearchResultItem
                      key={idx}
                      result={result}
                      activeResource={activeResource}
                      onSectionSelect={handleSectionSelect}
                      onClose={() => setShowSearchResults(false)}
                      fedRegInfo={fedRegInfo}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto relative">
          {/* Floating Font Size Control */}
          <div className="absolute top-2 right-4 z-10">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 h-7 px-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100/80 bg-white/60 backdrop-blur-sm rounded-md shadow-sm"
                  title="Change font size"
                >
                  <Type className="w-3.5 h-3.5" />
                  <span className="text-xs">
                    {resourcesFontSize === 'xs' ? 'XS' : resourcesFontSize === 'sm' ? 'SM' : resourcesFontSize === 'base' ? 'MD' : 'LG'}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-36">
                {RESOURCES_FONT_SIZE_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => setResourcesFontSize(option.value)}
                    className={`cursor-pointer ${resourcesFontSize === option.value ? 'bg-blue-50 text-blue-700' : ''}`}
                  >
                    <span style={{ fontSize: option.size }}>
                      {option.label}
                    </span>
                    {resourcesFontSize === option.value && (
                      <span className="ml-auto text-blue-600">✓</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="px-8 py-6 max-w-6xl" style={getFontSizeStyle()}>
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-gray-600">Loading...</div>
              </div>
            )}
            {!loading && activeResource === 'mpep' && <MpepContent selectedSection={selectedSection} sectionContent={sectionContent} onSectionSelect={handleSectionSelect} getFontSizeStyle={getFontSizeStyle} />}
            {!loading && activeResource === '37cfr' && <CfrContent selectedSection={selectedSection} sectionContent={sectionContent} onSectionSelect={handleSectionSelect} getFontSizeStyle={getFontSizeStyle} />}
            {!loading && activeResource === '35usc' && <UscContent selectedSection={selectedSection} sectionContent={sectionContent} uscInfo={uscInfo} onSectionSelect={handleSectionSelect} getFontSizeStyle={getFontSizeStyle} />}
            {!loading && activeResource === 'fedreg' && <FedRegContent selectedSection={selectedSection} fedRegInfo={fedRegInfo} onSectionSelect={handleSectionSelect} getFontSizeStyle={getFontSizeStyle} />}
            {!loading && activeResource === 'fees' && <FeesContent selectedSection={selectedSection} feeInfo={feeInfo} getFontSizeStyle={getFontSizeStyle} onSectionSelect={handleSectionSelect} />}
            {!loading && activeResource === 'abstract' && <AbstractContent selectedSection={selectedSection} getFontSizeStyle={getFontSizeStyle} />}
          </div>
        </div>
      </div>
    </div>
  );
}

// Navigation Components
interface NavigationProps {
  selectedSection: string;
  onSectionSelect: (section: string) => void;
  mpepInfo?: any;
  feeInfo?: any;
}

function MpepNavigation({ selectedSection, onSectionSelect, mpepInfo }: NavigationProps) {
  // Function to determine chapter number from section number
  const getChapterNumber = (sectionNum: string): string => {
    const num = parseInt(sectionNum);
    if (num < 200) return '100';
    if (num < 300) return '200';
    if (num < 400) return '300';
    if (num < 500) return '400';
    if (num < 600) return '500';
    if (num < 700) return '600';
    if (num < 800) return '700';
    if (num < 900) return '800';
    if (num < 1000) return '900';
    if (num < 1100) return '1000';
    if (num < 1200) return '1100';
    if (num < 1300) return '1200';
    if (num < 1400) return '1300';
    if (num < 1500) return '1400';
    if (num < 1600) return '1500';
    if (num < 1700) return '1600';
    if (num < 1800) return '1700';
    if (num < 2000) return '1900';
    if (num < 2100) return '2000';
    if (num < 2200) return '2100';
    if (num < 2300) return '2200';
    if (num < 2400) return '2300';
    if (num < 2500) return '2400';
    if (num < 2600) return '2500';
    if (num < 2700) return '2600';
    if (num < 2800) return '2700';
    return '2800';
  };

  // Chapter titles mapping
  const chapterTitles: Record<string, string> = {
    '100': 'Secrecy, Access, Dissemination',
    '200': 'Types and Status of Application',
    '300': 'Ownership and Assignment',
    '400': 'Representative of Applicant',
    '500': 'Receipt and Handling',
    '600': 'Parts, Form, and Content of Application',
    '700': 'Examination of Applications',
    '800': 'Restriction in Applications',
    '900': 'Prior Art, Classification',
    '1000': 'Matters Decided by Various USPTO Officials',
    '1100': 'Statutory Invention Registration',
    '1200': 'Appeal',
    '1300': 'Allowance and Issue',
    '1400': 'Correction of Patents',
    '1500': 'Design Patents',
    '1600': 'Plant Patents',
    '1700': 'Petition to Institute a Public Use Proceeding',
    '1900': 'Protest',
    '2000': 'Duty of Disclosure',
    '2100': 'Patentability',
    '2200': 'Citation of Prior Art and Written Disclosure',
    '2300': 'Interference',
    '2400': 'Biotechnology',
    '2500': 'Maintenance Fees',
    '2600': 'Optional Inter Partes Reexamination',
    '2700': 'Patent Terms',
    '2800': 'Supplemental Examination',
  };

  let chapters: any[] = [];

  // If we have mpepInfo data, parse it dynamically
  if (mpepInfo && typeof mpepInfo === 'object') {
    // mpepInfo structure: { "0100": { title: "...", sections: { "101": {...}, "102": {...} } } }
    // Chapter keys are like "0100", "0200", "1000", "1100", "2100" etc. - 4 digits ending in "00"
    chapters = Object.keys(mpepInfo)
      .filter(key => {
        // Check if key is 4 digits and ends with "00"
        return /^\d{4}$/.test(key) && key.endsWith('00');
      })
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map((chapterKey) => {
        const chapterData = mpepInfo[chapterKey];
        // Remove leading zeros to get chapter number: "0100" -> "100", "2100" -> "2100"
        const chapterNum = parseInt(chapterKey).toString();

        // Extract section numbers from the sections object
        const sectionNumbers = chapterData.sections
          ? Object.keys(chapterData.sections).sort((a, b) => parseInt(a) - parseInt(b))
          : [];

        return {
          id: chapterNum,
          number: chapterNum,
          title: chapterData.title || chapterTitles[chapterNum] || `Chapter ${chapterNum}`,
          sections: sectionNumbers
        };
      });
  } else {
    // Fallback to hardcoded chapters if no API data available
    chapters = []
  }

  // Helper function to check if a chapter is selected (itself or any of its sections)
  const isChapterSelected = (chapter: any) => {
    if (selectedSection === chapter.id) return true;
    if (chapter.sections) {
      return chapter.sections.some((section: string) => `mpep-${section}` === selectedSection);
    }
    return false;
  };

  return (
    <div className="space-y-2">
      {chapters.map((chapter) => {
        const chapterSelected = isChapterSelected(chapter);
        const hasSections = chapter.sections && chapter.sections.length > 0;
        return (
          <div key={chapter.id}>
            <button
              onClick={() => {
                // Toggle collapse if already selected
                if (selectedSection === chapter.id) {
                  onSectionSelect('');
                } else {
                  onSectionSelect(chapter.id);
                }
              }}
              className={`w-full text-left p-3 rounded-lg transition-all ${
                chapterSelected
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
              }`}
            >
              <div className={`text-xs mb-1 font-medium ${
                chapterSelected ? 'text-blue-100' : 'text-slate-400'
              }`}>
                Chapter {chapter.number}
              </div>
              <div className="text-sm font-medium leading-tight">{chapter.title}</div>
              {hasSections && (
                <div className={`text-xs mt-1 ${
                  chapterSelected ? 'text-blue-100' : 'text-slate-400'
                }`}>
                  {chapter.sections.length} sections
                </div>
              )}
            </button>
            {hasSections && chapterSelected && (
              <div className="ml-4 mt-1 space-y-1">
                {chapter.sections.map((section) => (
                  <button
                    key={section}
                    onClick={() => onSectionSelect(`mpep-${section}`)}
                    className={`w-full text-left p-2 rounded-md text-xs transition-all ${
                      selectedSection === `mpep-${section}`
                        ? 'bg-blue-500 text-white font-medium'
                        : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    MPEP §{section}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CfrNavigation({ selectedSection, onSectionSelect }: NavigationProps) {
  const [cfrData, setCfrData] = useState<any>(null);
  const [expandedParts, setExpandedParts] = useState<Record<string, boolean>>({});
  const [expandedSubparts, setExpandedSubparts] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetch('/cfr_info.json')
      .then(res => res.json())
      .then(data => setCfrData(data))
      .catch(err => console.error('Failed to load CFR structure:', err));
  }, []);

  if (!cfrData) {
    return <div className="p-4 text-white text-sm">Loading CFR structure...</div>;
  }

  // Find USPTO Chapter I
  const usptoChapter = cfrData.find((chapter: any) => chapter.number === 'I');
  if (!usptoChapter) {
    return <div className="p-4 text-white text-sm">USPTO chapter not found</div>;
  }

  const togglePart = (partKey: string) => {
    setExpandedParts(prev => ({ ...prev, [partKey]: !prev[partKey] }));
  };

  const toggleSubpart = (subpartKey: string) => {
    setExpandedSubparts(prev => ({ ...prev, [subpartKey]: !prev[subpartKey] }));
  };

  const handleSectionClick = (sectionNumber: string) => {
    const cleanNumber = sectionNumber.replace(/§\s*/, '').trim();
    onSectionSelect(`cfr-${cleanNumber}`);
  };

  return (
    <div className="space-y-1 text-sm">
      {/* Iterate through subchapters */}
      {usptoChapter.children?.map((subchapter: any, subIdx: number) => (
        <div key={subIdx}>
          <div className="text-xs font-semibold text-white text-opacity-90 mb-2 mt-3">
            {subchapter.name}
          </div>

          {/* Iterate through parts - numerically sorted */}
          {subchapter.children
            ?.slice()
            .sort((a: any, b: any) => {
              const numA = parseInt(a.number.split('-')[0]) || 0;
              const numB = parseInt(b.number.split('-')[0]) || 0;
              return numA - numB;
            })
            .map((part: any, partIdx: number) => {
              const partKey = `${subIdx}-${part.number}`;
              const hasSubparts = part.children?.some((c: any) => c.type === 'subpart');
              const directSections = part.children?.filter((c: any) => c.type === 'section') || [];

              return (
                <div key={partIdx} className="mb-1">
                  <button
                    onClick={() => togglePart(partKey)}
                    className={`w-full text-left p-2 rounded-lg transition-all ${
                      expandedParts[partKey]
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                    }`}
                  >
                    <div className={`text-xs mb-0.5 font-medium ${
                      expandedParts[partKey] ? 'text-purple-100' : 'text-slate-400'
                    }`}>
                      Part {part.number}
                    </div>
                    <div className="text-xs font-medium leading-tight">{part.name.replace(/^PART \d+—?/, '')}</div>
                  </button>

                  {expandedParts[partKey] && (
                    <div className="ml-3 mt-1 space-y-0.5">
                      {/* If part has direct sections (no subparts) */}
                      {!hasSubparts && directSections.length > 0 && directSections.map((section: any, secIdx: number) => (
                        <button
                          key={secIdx}
                          onClick={() => handleSectionClick(section.number)}
                          className={`w-full text-left py-1.5 px-2 rounded-md text-xs transition-all ${
                            selectedSection === `cfr-${section.number.replace(/§\s*/, '').trim()}`
                              ? 'bg-purple-500 text-white font-medium'
                              : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                          }`}
                        >
                          {section.name}
                        </button>
                      ))}

                      {/* If part has subparts */}
                      {hasSubparts && part.children
                        ?.filter((c: any) => c.type === 'subpart')
                        .map((subpart: any, subpartIdx: number) => {
                          const subpartKey = `${partKey}-${subpartIdx}`;
                          const sections = subpart.children?.filter((c: any) => c.type === 'section') || [];

                          if (sections.length === 0) return null;

                          return (
                            <div key={subpartIdx}>
                              <button
                                onClick={() => toggleSubpart(subpartKey)}
                                className={`w-full text-left py-1.5 px-2 rounded-md text-xs transition-all ${
                                  expandedSubparts[subpartKey]
                                    ? 'bg-slate-700 text-white'
                                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                                }`}
                              >
                                <div className="font-medium">{subpart.name}</div>
                                <div className="text-xs text-slate-400 mt-0.5">
                                  {sections.length} sections
                                </div>
                              </button>

                              {expandedSubparts[subpartKey] && (
                                <div className="ml-2 mt-0.5 space-y-0.5">
                                  {sections.map((section: any, secIdx: number) => (
                                    <button
                                      key={secIdx}
                                      onClick={() => handleSectionClick(section.number)}
                                      className={`w-full text-left py-1 px-2 rounded-md text-xs transition-all ${
                                        selectedSection === `cfr-${section.number.replace(/§\s*/, '').trim()}`
                                          ? 'bg-purple-500 text-white font-medium'
                                          : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                                      }`}
                                    >
                                      {section.name}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      ))}
    </div>
  );
}

function UscNavigation({ selectedSection, onSectionSelect }: NavigationProps) {
  const parts = [
    {
      id: 'I',
      number: 'I',
      title: 'United States Patent and Trademark Office',
      sections: '1-14',
      specificSections: ['1', '2', '3', '4', '6', '7', '8', '12']
    },
    {
      id: 'II',
      number: 'II',
      title: 'Patentability of Inventions and Grant of Patents',
      sections: '100-212',
      specificSections: ['101', '102', '103', '112', '119', '120', '121', '131', '132', '133', '134', '135', '141', '144', '145', '154', '156', '161', '171']
    },
    {
      id: 'III',
      number: 'III',
      title: 'Patents and Protection of Patent Rights',
      sections: '251-329',
      specificSections: ['251', '252', '253', '254', '255', '256', '257', '261', '262', '271', '272', '273', '282', '283', '284', '285', '287', '288', '289', '294', '295', '296', '297', '298', '299', '301', '302', '303', '304', '305', '306', '307', '311', '312', '313', '314', '315', '316', '317', '318', '319', '321', '322', '323', '324', '325', '326', '327', '328']
    },
    {
      id: 'IV',
      number: 'IV',
      title: 'Patent Cooperation Treaty',
      sections: '351-376',
      specificSections: ['351', '361', '362', '363', '364', '365', '366', '367', '368', '371', '372', '373', '374', '375', '376']
    },
  ];

  // Helper function to check if a section belongs to a part
  const isPartSelected = (part: any) => {
    if (selectedSection === part.id) return true;
    // Check if any of the part's sections are selected
    if (part.specificSections) {
      return part.specificSections.some((section: string) => `usc-${section}` === selectedSection);
    }
    return false;
  };

  return (
    <div className="space-y-2">
      {parts.map((part) => {
        const partSelected = isPartSelected(part);
        return (
          <div key={part.id}>
            <button
              onClick={() => {
                // Toggle collapse if already selected
                if (selectedSection === part.id) {
                  onSectionSelect('');
                } else {
                  onSectionSelect(part.id);
                }
              }}
              className={`w-full text-left p-3 rounded-lg transition-all ${
                partSelected
                  ? 'bg-teal-600 text-white shadow-md'
                  : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
              }`}
            >
              <div className={`text-xs mb-1 font-medium ${
                partSelected ? 'text-teal-100' : 'text-slate-400'
              }`}>
                Part {part.number}
              </div>
              <div className="text-sm font-medium leading-tight mb-1">{part.title}</div>
              <div className={`text-xs ${
                partSelected ? 'text-teal-100' : 'text-slate-400'
              }`}>
                §{part.sections}
              </div>
            </button>
            {part.specificSections && partSelected && (
              <div className="ml-4 mt-1 space-y-1">
                {part.specificSections.map((section) => (
                  <button
                    key={section}
                    onClick={() => onSectionSelect(`usc-${section}`)}
                    className={`w-full text-left p-2 rounded-md text-xs transition-all ${
                      selectedSection === `usc-${section}`
                        ? 'bg-teal-500 text-white font-medium'
                        : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    35 USC §{section}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function FedRegNavigation({ selectedSection, onSectionSelect, fedRegInfo }: NavigationProps & { fedRegInfo?: any }) {
  const [expandedYears, setExpandedYears] = useState<Record<string, boolean>>({});

  if (!fedRegInfo || (Array.isArray(fedRegInfo) && fedRegInfo.length === 0)) {
    return (
      <div className="p-4 text-white text-sm">
        Loading Federal Register data...
      </div>
    );
  }

  // Extract the actual entries from fed_reg_info property
  const actualData = fedRegInfo.fed_reg_info || fedRegInfo;

  // Convert to array if it's an object, preserving IDs
  let entriesArray: any[] = [];
  if (Array.isArray(actualData)) {
    entriesArray = actualData;
  } else if (typeof actualData === 'object') {
    // If it's an object where keys are IDs
    entriesArray = Object.entries(actualData).map(([key, value]: [string, any]) => {
      // If the value doesn't have an id, use the key as the id
      return { ...value, id: value.id || key };
    });
  }

  // Sort entries by year extracted from id (most recent first)
  const sortedEntries = entriesArray.sort((a: any, b: any) => {
    const matchA = a.id?.match(/^fed_reg_uspto_(\d{4})\//);
    const matchB = b.id?.match(/^fed_reg_uspto_(\d{4})\//);
    const yearA = matchA ? parseInt(matchA[1]) : 0;
    const yearB = matchB ? parseInt(matchB[1]) : 0;

    // If years are different, sort by year
    if (yearA !== yearB) {
      return yearB - yearA; // Most recent year first
    }

    // If years are the same, sort by timestamp
    const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return timeB - timeA;
  });

  // Group by year
  const groupedByYear = sortedEntries.reduce((acc: any, entry: any) => {
    const match = entry.id?.match(/^fed_reg_uspto_(\d{4})\//);
    const year = match ? match[1] : 'Unknown';
    if (!acc[year]) acc[year] = [];
    acc[year].push(entry);
    return acc;
  }, {});

  const toggleYear = (year: string) => {
    setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));
  };

  return (
    <div className="space-y-2">
      {Object.entries(groupedByYear)
        .sort(([a], [b]) => parseInt(b) - parseInt(a))
        .map(([year, entries]: [string, any]) => (
          <div key={year}>
            <button
              onClick={() => toggleYear(year)}
              className={`w-full text-left p-3 rounded-lg transition-all ${
                expandedYears[year]
                  ? 'bg-green-600 text-white shadow-md'
                  : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold">{year}</div>
                  <div className={`text-xs mt-1 ${
                    expandedYears[year] ? 'text-green-100' : 'text-slate-400'
                  }`}>
                    {entries.length} {entries.length === 1 ? 'notice' : 'notices'}
                  </div>
                </div>
                {expandedYears[year] ? (
                  <ChevronRight className="w-4 h-4 transform rotate-90 text-white" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                )}
              </div>
            </button>
            {expandedYears[year] && (
              <div className="ml-3 mt-1 space-y-1">
                {entries.map((entry: any) => (
                  <button
                    key={entry.id}
                    onClick={() => onSectionSelect(`fedreg-${entry.id}`)}
                    className={`w-full text-left p-2 rounded-md text-xs transition-all ${
                      selectedSection === `fedreg-${entry.id}`
                        ? 'bg-green-500 text-white font-medium'
                        : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    <div className="line-clamp-2">{entry.title}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

function FeesNavigation({ selectedSection, onSectionSelect, feeInfo }: NavigationProps) {
  // Use API data if available, otherwise use fallback categories
  const categories = feeInfo && Array.isArray(feeInfo) && feeInfo.length > 0
    ? feeInfo.map((item: any, index: number) => ({
        id: `fee-${index}`,
        label: item.category,
        generalCategory: item.general_category
      }))
    : [
        { id: 'filing', label: 'Basic Filing Fees', generalCategory: 'Patent fees' },
        { id: 'examination', label: 'Examination Fees', generalCategory: 'Patent fees' },
        { id: 'issue', label: 'Issue Fees', generalCategory: 'Patent fees' },
        { id: 'maintenance', label: 'Maintenance Fees', generalCategory: 'Patent fees' },
        { id: 'post-allowance', label: 'Post Allowance Fees', generalCategory: 'Patent fees' },
        { id: 'petition', label: 'Petition Fees', generalCategory: 'Patent fees' },
      ];

  return (
    <div className="space-y-2">
      {/* Fee Calculator Option */}
      <button
        onClick={() => onSectionSelect('fee-calculator')}
        className={`w-full text-left p-3 rounded-lg transition-all ${
          selectedSection === 'fee-calculator'
            ? 'bg-orange-600 text-white shadow-md'
            : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
        }`}
      >
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4" />
          <div className="text-sm font-semibold">Fee Calculator</div>
        </div>
        <div className={`text-xs mt-1 ${
          selectedSection === 'fee-calculator' ? 'text-orange-100' : 'text-slate-400'
        }`}>
          Calculate total fees
        </div>
      </button>

      <div className="border-t border-slate-700 my-2"></div>

      {categories.map((category) => (
        <button
          key={category.id}
          onClick={() => onSectionSelect(category.id)}
          className={`w-full text-left p-3 rounded-lg transition-all ${
            selectedSection === category.id
              ? 'bg-orange-600 text-white shadow-md'
              : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
          }`}
        >
          <div className="text-sm font-medium">{category.label}</div>
          {category.generalCategory && (
            <div className={`text-xs mt-1 ${
              selectedSection === category.id ? 'text-orange-100' : 'text-slate-400'
            }`}>
              {category.generalCategory}
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

function AbstractNavigation({ selectedSection, onSectionSelect }: NavigationProps) {
  const sections = [
    { id: 'framework', label: '§101 Framework' },
    { id: 'resources', label: 'USPTO Resources' },
    { id: 'precedents', label: 'Court Precedents' },
  ];

  return (
    <div className="space-y-2">
      <h2 className="text-xs font-bold tracking-wide uppercase text-slate-400 mb-4">Navigation</h2>
      {sections.map((section) => (
        <button
          key={section.id}
          onClick={() => onSectionSelect(section.id)}
          className={`w-full text-left p-3 rounded-lg transition-all ${
            selectedSection === section.id
              ? 'bg-indigo-600 text-white shadow-md'
              : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
          }`}
        >
          <div className="text-sm font-medium">{section.label}</div>
        </button>
      ))}
    </div>
  );
}

// Fee Calculator Component
interface FeeItem {
  id: string;
  name: string;
  category: string;
  undiscounted: number;
  small: number;
  micro: number;
}

interface CustomFeePackage {
  package_id: string;
  package_name: string;
  fee_ids: string[];
  created_at: string;
}

function FeeCalculator({ feeInfo, getFontSizeStyle }: { feeInfo: any; getFontSizeStyle?: () => React.CSSProperties }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [entityType, setEntityType] = useState<'undiscounted' | 'small' | 'micro'>('undiscounted');
  const [selectedFees, setSelectedFees] = useState<Set<string>>(new Set());
  const [rceType, setRceType] = useState<'first' | 'subsequent'>('first');
  const [customPackages, setCustomPackages] = useState<CustomFeePackage[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newPackageName, setNewPackageName] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState<CustomFeePackage | null>(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Parse fee amount from string (e.g., "$1,200" -> 1200)
  const parseFeeAmount = (feeStr: string): number => {
    if (!feeStr) return 0;
    const cleaned = feeStr.toString().replace(/[$,]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Extract all fees from feeInfo
  const allFees: FeeItem[] = React.useMemo(() => {
    if (!feeInfo || !Array.isArray(feeInfo) || feeInfo.length === 0) {
      // Fallback to static fee data
      const fallbackCategories = [
        {
          category: 'Basic Filing Fees',
          fees: [
            { name: 'Utility application filing fee', undiscounted: '$320', small: '$160', micro: '$80' },
            { name: 'Design application filing fee', undiscounted: '$220', small: '$110', micro: '$55' },
            { name: 'Plant application filing fee', undiscounted: '$220', small: '$110', micro: '$55' },
          ],
        },
        {
          category: 'Examination Fees',
          fees: [
            { name: 'Utility examination fee', undiscounted: '$800', small: '$400', micro: '$200' },
            { name: 'Design examination fee', undiscounted: '$680', small: '$340', micro: '$170' },
            { name: 'Plant examination fee', undiscounted: '$680', small: '$340', micro: '$170' },
          ],
        },
        {
          category: 'Issue Fees',
          fees: [
            { name: 'Utility issue fee', undiscounted: '$1,200', small: '$600', micro: '$300' },
            { name: 'Design issue fee', undiscounted: '$880', small: '$440', micro: '$220' },
            { name: 'Plant issue fee', undiscounted: '$880', small: '$440', micro: '$220' },
          ],
        },
      ];

      const fees: FeeItem[] = [];
      fallbackCategories.forEach((categoryData, categoryIndex) => {
        categoryData.fees.forEach((fee: any, feeIndex: number) => {
          fees.push({
            id: `fallback-${categoryIndex}-${feeIndex}`,
            name: fee.name,
            category: categoryData.category,
            undiscounted: parseFeeAmount(fee.undiscounted),
            small: parseFeeAmount(fee.small),
            micro: parseFeeAmount(fee.micro),
          });
        });
      });
      return fees;
    }

    const fees: FeeItem[] = [];
    feeInfo.forEach((categoryData: any, categoryIndex: number) => {
      if (categoryData.fees && Array.isArray(categoryData.fees)) {
        categoryData.fees.forEach((fee: any, feeIndex: number) => {
          // Try to find fee description and amounts
          const feeKeys = Object.keys(fee);

          // Skip if "Back to top" or empty
          if (feeKeys.length === 0 || (feeKeys.length === 1 && feeKeys[0] === 'Back to top')) {
            return;
          }

          // Find specific columns by name patterns
          const descField = feeKeys.find(k =>
            k.toLowerCase().includes('description') ||
            k.toLowerCase() === 'description'
          );

          const smallField = feeKeys.find(k =>
            k.toLowerCase().includes('small') && k.toLowerCase().includes('fee')
          );

          const microField = feeKeys.find(k =>
            k.toLowerCase().includes('micro') && k.toLowerCase().includes('fee')
          );

          // Find undiscounted field - must be exactly "Fee" or similar, not "Fee code" or "Small entity fee"
          const undiscountedField = feeKeys.find(k => {
            const lower = k.toLowerCase().trim();
            return lower === 'fee' ||
                   lower === 'undiscounted' ||
                   lower === 'standard fee' ||
                   (lower.includes('fee') && !lower.includes('small') && !lower.includes('micro') && !lower.includes('code') && !lower.includes('entity'));
          });

          // If we can't find fields by name, use positional matching
          // Typical order: Fee code, 37 CFR §, Description, Fee, Small entity fee, Micro entity fee
          let name = '';
          let undiscounted = 0, small = 0, micro = 0;

          if (descField) {
            name = fee[descField]?.toString() || '';
          } else {
            // Try to find description field (usually index 2, but could be 0 if simplified)
            const nonEmptyKeys = feeKeys.filter(k => k !== 'Back to top' && fee[k]);
            if (nonEmptyKeys.length >= 2) {
              // Assume description is the field that doesn't look like a number
              const descKey = nonEmptyKeys.find(k => {
                const val = fee[k]?.toString() || '';
                return !val.match(/^[\d.,\s$]+$/);
              });
              if (descKey) {
                name = fee[descKey]?.toString() || '';
              }
            }
          }

          // Extract fee amounts
          if (undiscountedField) {
            undiscounted = parseFeeAmount(fee[undiscountedField]);
          }
          if (smallField) {
            small = parseFeeAmount(fee[smallField]);
          }
          if (microField) {
            micro = parseFeeAmount(fee[microField]);
          }

          // If named fields didn't work, try positional (starting from the end, which is more reliable)
          if (undiscounted === 0 && small === 0 && micro === 0) {
            const numericKeys = feeKeys.filter(k => {
              const val = fee[k]?.toString() || '';
              return val.match(/^[\d.,\s$]+$/) && parseFeeAmount(val) > 0;
            });

            if (numericKeys.length >= 3) {
              // Last 3 numeric fields should be: Fee, Small entity fee, Micro entity fee
              micro = parseFeeAmount(fee[numericKeys[numericKeys.length - 1]]);
              small = parseFeeAmount(fee[numericKeys[numericKeys.length - 2]]);
              undiscounted = parseFeeAmount(fee[numericKeys[numericKeys.length - 3]]);
            } else if (numericKeys.length === 2) {
              small = parseFeeAmount(fee[numericKeys[numericKeys.length - 1]]);
              undiscounted = parseFeeAmount(fee[numericKeys[numericKeys.length - 2]]);
            } else if (numericKeys.length === 1) {
              undiscounted = parseFeeAmount(fee[numericKeys[0]]);
            }
          }

          // Only add if we have a name and at least one fee amount
          if (name && (undiscounted > 0 || small > 0 || micro > 0)) {
            fees.push({
              id: `${categoryIndex}-${feeIndex}`,
              name: name,
              category: categoryData.category || 'Uncategorized',
              undiscounted: undiscounted,
              small: small,
              micro: micro,
            });
          }
        });
      }
    });
    return fees;
  }, [feeInfo]);

  // Filter out the backend's electronic filing for small entities fee - we show the message instead
  // Also filter RCE fees based on selection (show only 1st or 2nd+ based on rceType)
  const feesWithElectronic = React.useMemo(() => {
    return allFees.filter(f => {
      const nameLower = f.name.toLowerCase();

      // Filter out electronic filing for small entities
      if (nameLower.includes('electronic filing for small entities')) {
        return false;
      }

      // Filter RCE fees based on selection
      if ((nameLower.includes('rce') || (nameLower.includes('request') && nameLower.includes('continued'))) &&
          nameLower.includes('examination')) {
        if (rceType === 'first') {
          // Show only 1st request, hide 2nd and subsequent
          if (nameLower.includes('2nd') || nameLower.includes('subsequent')) {
            return false;
          }
        } else {
          // Show only 2nd and subsequent, hide 1st
          if (nameLower.includes('1st')) {
            return false;
          }
        }
      }

      return true;
    });
  }, [allFees, rceType]);

  // Filter fees based on search term
  const filteredFees = feesWithElectronic.filter(fee =>
    fee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fee.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate total
  const total = Array.from(selectedFees).reduce((sum, feeId) => {
    const fee = feesWithElectronic.find(f => f.id === feeId);
    if (fee) {
      return sum + fee[entityType];
    }
    return sum;
  }, 0);

  const toggleFee = (feeId: string) => {
    const newSelected = new Set(selectedFees);
    if (newSelected.has(feeId)) {
      newSelected.delete(feeId);
    } else {
      newSelected.add(feeId);
    }
    setSelectedFees(newSelected);
  };

  const clearAll = () => {
    setSelectedFees(new Set());
  };

  // Auto-update selected RCE fee when RCE type changes
  React.useEffect(() => {
    const currentSelectedList = Array.from(selectedFees)
      .map(id => allFees.find(f => f.id === id))
      .filter(Boolean) as FeeItem[];

    const hasRce = currentSelectedList.some(fee => {
      const nameLower = fee.name.toLowerCase();
      return (nameLower.includes('rce') || (nameLower.includes('request') && nameLower.includes('continued'))) &&
        nameLower.includes('examination');
    });

    if (hasRce) {
      // Find the appropriate RCE fee based on rceType
      const targetRceFee = allFees.find(f => {
        const nameLower = f.name.toLowerCase();
        const isRce = (nameLower.includes('rce') || (nameLower.includes('request') && nameLower.includes('continued'))) &&
          nameLower.includes('examination');

        if (!isRce) return false;

        if (rceType === 'first') {
          return !nameLower.includes('2nd') && !nameLower.includes('subsequent');
        } else {
          return nameLower.includes('2nd') || nameLower.includes('subsequent');
        }
      });

      if (targetRceFee) {
        // Remove all RCE fees and add the target one
        const newSelected = new Set(selectedFees);
        currentSelectedList.forEach(fee => {
          const nameLower = fee.name.toLowerCase();
          if ((nameLower.includes('rce') || (nameLower.includes('request') && nameLower.includes('continued'))) &&
              nameLower.includes('examination')) {
            newSelected.delete(fee.id);
          }
        });
        newSelected.add(targetRceFee.id);
        setSelectedFees(newSelected);
      }
    }
  }, [rceType]); // Only run when rceType changes

  const selectedFeesList = Array.from(selectedFees)
    .map(id => feesWithElectronic.find(f => f.id === id))
    .filter(Boolean) as FeeItem[];

  // Check if an RCE fee is selected
  const hasRceFeeSelected = selectedFeesList.some(fee => {
    if (!fee) return false;
    const nameLower = fee.name.toLowerCase();
    return (nameLower.includes('rce') || (nameLower.includes('request') && nameLower.includes('continued'))) &&
      nameLower.includes('examination');
  });

  // Load custom packages on mount
  useEffect(() => {
    loadCustomPackages();
  }, []);

  const loadCustomPackages = async () => {
    try {
      const response = await userApi.fetchFeePackages();

      // Handle different response formats
      let packages = [];
      if (Array.isArray(response)) {
        packages = response;
      } else if (response && Array.isArray(response.packages)) {
        packages = response.packages;
      }

      setCustomPackages(packages);
    } catch (error) {
      console.error('Failed to load custom packages:', error);
      setCustomPackages([]);
    }
  };

  const handleSavePackage = async () => {
    if (!newPackageName.trim()) {
      setErrorMessage('Please enter a package name');
      setShowErrorDialog(true);
      return;
    }
    if (selectedFees.size === 0) {
      setErrorMessage('Please select at least one fee');
      setShowErrorDialog(true);
      return;
    }

    try {
      const feeIds = Array.from(selectedFees);
      await userApi.saveFeePackage(newPackageName, feeIds);
      setNewPackageName('');
      setShowSaveDialog(false);
      await loadCustomPackages();
    } catch (error) {
      console.error('Failed to save package:', error);
      setErrorMessage('Failed to save package. Please try again.');
      setShowErrorDialog(true);
    }
  };

  const handleLoadPackage = (pkg: CustomFeePackage) => {
    setSelectedFees(new Set(pkg.fee_ids));
  };

  const handleDeletePackage = async () => {
    if (!packageToDelete) return;

    try {
      await userApi.deleteFeePackage(packageToDelete.package_id);

      // Reload the packages list before closing the dialog
      await loadCustomPackages();

      // Then close the dialog
      setShowDeleteDialog(false);
      setPackageToDelete(null);
    } catch (error) {
      console.error('Failed to delete package:', error);
      setShowDeleteDialog(false);
      setPackageToDelete(null);
      setErrorMessage('Failed to delete package. Please try again.');
      setShowErrorDialog(true);
    }
  };

  return (
    <div style={getFontSizeStyle?.()}>
      <div className="mb-6">
        <h1 className="text-2xl text-gray-900 mb-1 flex items-center gap-2">
          <Calculator className="w-6 h-6 text-green-600" />
          USPTO Fee Calculator
        </h1>
        <div className="text-gray-600">Select fees to calculate the total cost</div>
      </div>

      {/* Entity Type Selector */}
      <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-gray-900">Entity Type:</div>
          <div className="flex gap-2">
            <button
              onClick={() => setEntityType('undiscounted')}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                entityType === 'undiscounted'
                  ? 'bg-green-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:border-green-400'
              }`}
            >
              Undiscounted
            </button>
            <button
              onClick={() => setEntityType('small')}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                entityType === 'small'
                  ? 'bg-green-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:border-green-400'
              }`}
            >
              Small (60% off)
            </button>
            <button
              onClick={() => setEntityType('micro')}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                entityType === 'micro'
                  ? 'bg-green-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:border-green-400'
              }`}
            >
              Micro (80% off)
            </button>
          </div>
        </div>
      </div>

      {/* Quick Presets */}
      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="text-sm font-medium text-gray-900 mb-2">Quick Presets:</div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              let preset = feesWithElectronic.filter(f => {
                const nameLower = f.name.toLowerCase();
                // Include utility filing, search, and examination fees
                // Also include "basic filing fee" even if it doesn't mention "utility"
                // Exclude reissue, design, and plant fees
                return (
                  (nameLower.includes('utility') && (nameLower.includes('filing') || nameLower.includes('search') || nameLower.includes('examination'))) ||
                  (nameLower.includes('basic filing fee') && !nameLower.includes('design') && !nameLower.includes('plant') && !nameLower.includes('reissue'))
                ) && !nameLower.includes('reissue');
              });

              // Always exclude the non-electronic filing surcharge (the $400 additional fee)
              // Only exclude fees that START with "non-electronic" or explicitly mention "additional fee for paper"
              preset = preset.filter(f => {
                const nameLower = f.name.toLowerCase();
                return !nameLower.startsWith('non-electronic') &&
                       !nameLower.includes('additional fee for applications filed in paper') &&
                       !(nameLower.includes('non-electronic filing fee') && nameLower.includes('additional'));
              });

              setSelectedFees(new Set(preset.map(f => f.id)));
            }}
            className="px-3 py-1.5 bg-white border border-blue-300 rounded-md text-xs font-medium text-gray-700 hover:bg-blue-100 hover:border-blue-400 transition-colors"
          >
            📝 Utility Filing
          </button>
          <button
            onClick={() => {
              const preset = allFees.filter(f => {
                const nameLower = f.name.toLowerCase();

                // Basic filing fee - Design (not CPA or reissue)
                const isBasicFiling = nameLower.includes('basic filing fee') &&
                  nameLower.includes('design') &&
                  !nameLower.includes('cpa') &&
                  !nameLower.includes('reissue');

                // Design search fee (regular or CPA)
                const isSearchFee = nameLower.includes('design') &&
                  nameLower.includes('search fee') &&
                  !nameLower.includes('reissue');

                // Design examination fee (regular or CPA)
                const isExamFee = nameLower.includes('design') &&
                  nameLower.includes('examination fee') &&
                  !nameLower.includes('reissue');

                return isBasicFiling || isSearchFee || isExamFee;
              });
              setSelectedFees(new Set(preset.map(f => f.id)));
            }}
            className="px-3 py-1.5 bg-white border border-blue-300 rounded-md text-xs font-medium text-gray-700 hover:bg-blue-100 hover:border-blue-400 transition-colors"
          >
            🎨 Design Filing
          </button>
          <button
            onClick={() => {
              const preset = allFees.filter(f => {
                const nameLower = f.name.toLowerCase();
                // Provisional application filing fee (exclude surcharges, late fees, cover sheet)
                return nameLower.includes('provisional') &&
                  nameLower.includes('filing fee') &&
                  !nameLower.includes('surcharge') &&
                  !nameLower.includes('late') &&
                  !nameLower.includes('cover sheet');
              });
              setSelectedFees(new Set(preset.map(f => f.id)));
            }}
            className="px-3 py-1.5 bg-white border border-blue-300 rounded-md text-xs font-medium text-gray-700 hover:bg-blue-100 hover:border-blue-400 transition-colors"
          >
            ⚡ Provisional
          </button>
          <button
            onClick={() => {
              const preset = allFees.filter(f =>
                f.name.toLowerCase().includes('utility') &&
                f.name.toLowerCase().includes('issue')
              );
              setSelectedFees(new Set(preset.map(f => f.id)));
            }}
            className="px-3 py-1.5 bg-white border border-blue-300 rounded-md text-xs font-medium text-gray-700 hover:bg-blue-100 hover:border-blue-400 transition-colors"
          >
            ✓ Utility Issue
          </button>
          <button
            onClick={() => {
              const preset = feesWithElectronic.filter(f => {
                const nameLower = f.name.toLowerCase();
                return (nameLower.includes('rce') ||
                  (nameLower.includes('request') && nameLower.includes('continued'))) &&
                  nameLower.includes('examination');
              });
              setSelectedFees(new Set(preset.map(f => f.id)));
            }}
            className="px-3 py-1.5 bg-white border border-blue-300 rounded-md text-xs font-medium text-gray-700 hover:bg-blue-100 hover:border-blue-400 transition-colors"
          >
            🔄 RCE
          </button>
        </div>
        <div className="mt-2 text-xs text-blue-700 bg-blue-100 px-3 py-1.5 rounded">
          ℹ️ Presets assume electronic filing via USPTO's filing system. Non-electronic (paper) filing fees available in fee list if needed.
        </div>
      </div>

      {/* My Custom Packages */}
      {customPackages.length > 0 && (
        <div className="mb-4 bg-purple-50 border border-purple-200 rounded-lg p-3">
          <div className="text-sm font-medium text-gray-900 mb-2">My Custom Packages:</div>
          <div className="flex flex-wrap gap-2">
            {customPackages.map((pkg) => (
              <div key={pkg.package_id} className="flex items-center gap-1 bg-white border border-purple-300 rounded-md">
                <button
                  onClick={() => handleLoadPackage(pkg)}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-purple-100 rounded-l-md transition-colors"
                >
                  📦 {pkg.package_name}
                </button>
                <button
                  onClick={() => {
                    setPackageToDelete(pkg);
                    setShowDeleteDialog(true);
                  }}
                  className="px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 border-l border-purple-200 rounded-r-md transition-colors"
                  title="Delete package"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save Current Selection */}
      {selectedFees.size > 0 && (
        <div className="mb-4">
          <Button
            onClick={() => setShowSaveDialog(true)}
            variant="outline"
            className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
          >
            💾 Save Current Selection as Package
          </Button>
        </div>
      )}

      {/* Save Package Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Save Fee Package</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package Name
              </label>
              <Input
                type="text"
                value={newPackageName}
                onChange={(e) => setNewPackageName(e.target.value)}
                placeholder="e.g., My Typical Filing"
                className="w-full"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSavePackage();
                  }
                }}
              />
            </div>
            <div className="text-sm text-gray-600 mb-4">
              This will save {selectedFees.size} fee{selectedFees.size !== 1 ? 's' : ''} to this package.
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                onClick={() => {
                  setShowSaveDialog(false);
                  setNewPackageName('');
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSavePackage}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                Save Package
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Package Confirmation Dialog */}
      {showDeleteDialog && packageToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Fee Package</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete the package "{packageToDelete.package_name}"? This action cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setPackageToDelete(null);
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeletePackage}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete Package
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Error Dialog */}
      {showErrorDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4 text-red-600">Error</h3>
            <p className="text-gray-700 mb-6">{errorMessage}</p>
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setShowErrorDialog(false);
                  setErrorMessage('');
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white"
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search fees by name or category..."
            className="pl-10"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Fee Selection List */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="font-medium text-gray-900">
                  Available Fees ({filteredFees.length})
                </div>
                {selectedFees.size > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAll}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Clear All
                  </Button>
                )}
              </div>
            </div>

            <ScrollArea className="h-[600px]">
              <div className="divide-y divide-gray-200">
                {filteredFees.map((fee) => (
                  <label
                    key={fee.id}
                    className="p-4 hover:bg-gray-50 transition-colors cursor-pointer flex items-start gap-3"
                  >
                    <Checkbox
                      checked={selectedFees.has(fee.id)}
                      onCheckedChange={() => toggleFee(fee.id)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900">{fee.name}</div>
                      <div className="text-sm text-gray-500 mt-1">{fee.category}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-semibold text-gray-900">
                        ${fee[entityType].toLocaleString()}
                      </div>
                      {entityType !== 'undiscounted' && (
                        <div className="text-xs text-gray-500 line-through">
                          ${fee.undiscounted.toLocaleString()}
                        </div>
                      )}
                    </div>
                  </label>
                ))}

                {filteredFees.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <div>No fees found matching your search</div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* Summary Panel */}
        <div className="lg:col-span-1">
          <div className="sticky top-4">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-green-600 px-4 py-3 text-white">
                <div className="font-semibold">Fee Summary</div>
                <div className="text-sm opacity-90">
                  {selectedFees.size} {selectedFees.size === 1 ? 'fee' : 'fees'} selected
                </div>
              </div>

              <div className="p-4">
                {selectedFeesList.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <div className="text-sm">No fees selected</div>
                    <div className="text-xs mt-1">Select fees from the list to calculate total</div>
                  </div>
                ) : (
                  <>
                    <div className="max-h-[500px] overflow-y-auto mb-4">
                      <div className="space-y-2">
                        {selectedFeesList.map((fee) => {
                          // Check if this is a UTILITY basic filing fee for small entity (not electronic version)
                          const isBasicFilingSmall = entityType === 'small' &&
                            fee.name.toLowerCase().includes('basic filing') &&
                            fee.name.toLowerCase().includes('utility') &&
                            !fee.name.toLowerCase().includes('electronic filing for small') &&
                            !fee.name.toLowerCase().includes('alternative');

                          return (
                            <div key={fee.id} className="flex items-start gap-2 text-sm">
                              <button
                                onClick={() => toggleFee(fee.id)}
                                className="text-gray-400 hover:text-red-600 flex-shrink-0 mt-0.5"
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <div className="flex-1 min-w-0">
                                <div className="text-gray-700 leading-tight">{fee.name}</div>
                                <div className="text-xs text-gray-500">{fee.category}</div>
                                {isBasicFilingSmall && (
                                  <div className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded mt-1 border border-green-200">
                                    💡 Only $70 when filed electronically via USPTO system (§ 1.27(b)(2))
                                  </div>
                                )}
                              </div>
                              <div className="text-gray-900 font-medium flex-shrink-0">
                                ${fee[entityType].toLocaleString()}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm text-gray-600">Entity Type:</div>
                        <div className="text-sm font-medium text-gray-900">
                          {entityType === 'undiscounted' && 'Undiscounted'}
                          {entityType === 'small' && 'Small Entity'}
                          {entityType === 'micro' && 'Micro Entity'}
                        </div>
                      </div>

                      {/* RCE Type Selector - Only show when RCE fee is selected */}
                      {hasRceFeeSelected && (
                        <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
                          <div className="text-sm text-gray-600">RCE Type:</div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setRceType('first')}
                              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                rceType === 'first'
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-white border border-gray-300 text-gray-700 hover:border-purple-400'
                              }`}
                            >
                              1st
                            </button>
                            <button
                              onClick={() => setRceType('subsequent')}
                              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                rceType === 'subsequent'
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-white border border-gray-300 text-gray-700 hover:border-purple-400'
                              }`}
                            >
                              2nd+
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-2">
                        <div className="text-lg font-semibold text-gray-900">Total:</div>
                        <div className="text-2xl font-bold text-green-600">
                          ${total.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Info Box */}
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
              <div className="font-medium text-blue-900 mb-2">About Entity Types</div>
              <div className="space-y-1 text-blue-800">
                <div><strong>Small Entity:</strong> 60% fee reduction for entities with ≤500 employees</div>
                <div><strong>Micro Entity:</strong> 80% fee reduction for qualifying small entities</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Content Components
interface ContentProps {
  selectedSection: string;
  sectionContent?: any;
  onSectionSelect?: (section: string) => void;
  getFontSizeStyle?: () => React.CSSProperties;
}

interface MpepContentProps extends ContentProps {
  onSectionSelect?: (section: string) => void;
}

// Helper function to convert relative MPEP image URLs to absolute USPTO URLs
function processMpepHtml(html: string): string {
  if (!html) return html;

  const baseUrl = 'https://www.uspto.gov/web/offices/pac/mpep/';

  // Replace relative image src attributes with absolute URLs
  let processed = html.replace(
    /<img([^>]*?)src=["'](?!https?:\/\/)([^"']+)["']/gi,
    (match, beforeSrc, relativePath) => {
      // Clean up the relative path (remove leading ./ or /)
      const cleanPath = relativePath.replace(/^\.?\//, '');
      const absoluteUrl = baseUrl + cleanPath;
      return `<img${beforeSrc}src="${absoluteUrl}"`;
    }
  );

  // Remove hyperlinks but keep the text content
  processed = processed.replace(/<a[^>]*>(.*?)<\/a>/gi, '$1');

  return processed;
}

function MpepContent({ selectedSection, sectionContent, onSectionSelect, getFontSizeStyle }: MpepContentProps) {
  // Show specific section content if selected
  if (selectedSection && selectedSection.startsWith('mpep-')) {
    const sectionNum = selectedSection.replace('mpep-', '');
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl text-gray-900 mb-1">
            MPEP §{sectionNum}{sectionContent?.error ? ' not found' : ''}
          </h1>
          <div className="text-sm text-gray-600">Manual of Patent Examining Procedure</div>
        </div>
        <div className="mb-6">
          {sectionContent ? (
            <div className="mpep-content prose max-w-none" style={getFontSizeStyle?.()}>
              {sectionContent.error ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="text-6xl mb-4">📚</div>
                  <h3 className="text-lg font-semibold text-gray-700">Section not found</h3>
                </div>
              ) : sectionContent.content ? (
                <div dangerouslySetInnerHTML={{ __html: processMpepHtml(sectionContent.content) }} />
              ) : typeof sectionContent === 'string' ? (
                <div dangerouslySetInnerHTML={{ __html: processMpepHtml(sectionContent) }} />
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="text-6xl mb-4">❓</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Unexpected Content Format</h3>
                  <p className="text-gray-600">
                    The section content could not be displayed properly.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-700 leading-relaxed" style={getFontSizeStyle?.()}>
              Loading section content...
            </p>
          )}
        </div>
      </div>
    );
  }

  // Default overview content
  return (
    <div style={getFontSizeStyle?.()}>
      <div className="mb-6">
        <h1 className="text-2xl text-gray-900 mb-1">Manual of Patent Examining Procedure (MPEP)</h1>
        <div className="text-gray-600">Current Edition: Ninth Edition, Revision 01.2024</div>
      </div>

      <div className="mb-8 border-l-4 border-blue-600 pl-4">
        <h2 className="text-lg text-gray-900 mb-3">About the MPEP</h2>
        <p className="text-gray-700 leading-relaxed mb-3">
          The Manual of Patent Examining Procedure (MPEP) is published by the United States Patent and Trademark Office (USPTO) as a guide for patent examiners and applicants on the interpretation of patent laws and regulations. The MPEP is not legally binding, but it represents the USPTO's interpretation of the patent statutes, case law, and regulations.
        </p>
        <p className="text-gray-700 leading-relaxed">
          The MPEP is organized into chapters covering various aspects of patent examination, from the initial filing through final disposition. It serves as the primary reference for understanding USPTO practice and procedure.
        </p>
      </div>

      <div>
        <h2 className="text-lg text-gray-900 mb-4 border-l-4 border-blue-600 pl-4">Key Sections</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <button
            onClick={() => onSectionSelect?.('mpep-706')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-50 text-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">706</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                  Rejection of Claims
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Types of rejections and how to make them
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('mpep-714')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-50 text-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">714.12</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                  Amendments After Final
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Amendments after final rejection
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('mpep-802')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-50 text-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">802</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                  Restriction Requirements
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              When restriction is proper
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('mpep-1207')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-50 text-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">1207</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                  Examiner's Answer
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Examiner's answer on appeal
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('mpep-2131')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-50 text-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">2131</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                  Anticipation
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              102 novelty rejections and prior art
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('mpep-2141')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-50 text-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">2141</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                  Patentable Subject Matter
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Laws of nature, natural phenomena & abstract ideas
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('mpep-2143')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-50 text-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">2143</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                  Computer Inventions
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Software and business method patents
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('mpep-2144')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-50 text-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">2144</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                  Obviousness
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              103 obviousness analysis (Graham factors)
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('mpep-2163')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-50 text-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">2163</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                  Written Description
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              112(a) written description requirement
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('mpep-2173')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-50 text-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">2173</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                  Enablement
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              112(a) enablement requirement
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('mpep-608')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-50 text-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">608.01</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                  Specification Requirements
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Form and content of specification
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('mpep-2258')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-50 text-blue-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">2258</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                  Scope of <i>Ex Parte</i> Reexamination
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Determining scope and content of prior art
            </p>
          </button>
        </div>
      </div>
    </div>
  );
}

// CFR XML Renderer Component
function CfrXmlRenderer({ xmlString, sectionNumber }: { xmlString: string; sectionNumber?: string }) {
  const parseXML = (xmlStr: string) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(`<div>${xmlStr}</div>`, 'application/xml');
    return xmlDoc.documentElement;
  };

  let lastSubsectionType: string | null = null;

  const getIndentLevel = (subsectionId: string): number => {
    const isAmbiguous = /^[ivxIVX]$/.test(subsectionId);

    if (isAmbiguous && lastSubsectionType === 'letter') {
      return 1;
    }

    if (/^(i{2,3}|iv|vi{1,3}|ix|xi{0,3}|xiv|xv|xvi{0,3}|xix|xx)$/i.test(subsectionId) ||
        (isAmbiguous && lastSubsectionType === 'roman')) {
      lastSubsectionType = 'roman';
      return 3;
    } else if (/^[a-z]$/.test(subsectionId)) {
      lastSubsectionType = 'letter';
      return 1;
    } else if (/^[0-9]+$/.test(subsectionId)) {
      lastSubsectionType = 'number';
      return 2;
    } else if (/^[A-Z]$/.test(subsectionId)) {
      lastSubsectionType = 'upper';
      return 4;
    }

    return 0;
  };

  const renderNode = (node: Node, index: number, secNum?: string): React.ReactNode => {
    if (!node || node.nodeType !== 1) {
      if (node.nodeType === 3) {
        return node.textContent;
      }
      return null;
    }

    const element = node as Element;
    const tagName = element.tagName.toLowerCase();
    const children = Array.from(element.childNodes).map((child, idx) => renderNode(child, idx, secNum));

    switch (tagName) {
      case 'head':
        return (
          <h2 key={index} className="text-xl font-bold text-gray-900 mb-4 mt-6">
            {children}
          </h2>
        );
      case 'p':
        const paragraphText = element.textContent || '';
        const subsectionMatch = paragraphText.match(/^\s*\(([a-z0-9]+)\)\s/i);
        let paragraphId: string | undefined = undefined;
        let indentLevel = 0;

        if (subsectionMatch) {
          const subsectionId = subsectionMatch[1];
          if (secNum) {
            paragraphId = `${secNum}-${subsectionId}`;
          }
          indentLevel = getIndentLevel(subsectionId);
        }

        const paddingClass = indentLevel > 0 ? `pl-${indentLevel * 8}` : '';

        return (
          <p
            key={index}
            id={paragraphId}
            className={`mb-4 text-gray-800 leading-relaxed ${paddingClass}`}
          >
            {children}
          </p>
        );
      case 'i':
        return <i key={index} className="italic">{children}</i>;
      case 'b':
        return <b key={index} className="font-bold">{children}</b>;
      case 'div':
        return <div key={index} className="my-4">{children}</div>;
      case 'table':
        return (
          <table key={index} className="table-auto border-collapse w-full my-4 border border-gray-300">
            {children}
          </table>
        );
      case 'thead':
        return <thead key={index} className="bg-gray-100">{children}</thead>;
      case 'tbody':
        return <tbody key={index}>{children}</tbody>;
      case 'tr':
        return <tr key={index} className="border-t border-gray-300">{children}</tr>;
      case 'th':
        return <th key={index} className="px-4 py-2 border border-gray-300 text-left font-semibold">{children}</th>;
      case 'td':
        return <td key={index} className="px-4 py-2 border border-gray-300">{children}</td>;
      case 'extract':
        return <div key={index} className="border-l-4 border-gray-400 pl-4 text-gray-600 italic my-2">{children}</div>;
      default:
        return <span key={index}>{children}</span>;
    }
  };

  const rootNode = parseXML(xmlString);
  return <div className="cfr-content">{renderNode(rootNode, 0, sectionNumber)}</div>;
}

interface CfrContentProps extends ContentProps {
  onSectionSelect?: (section: string) => void;
}

function CfrContent({ selectedSection, sectionContent, onSectionSelect, getFontSizeStyle }: CfrContentProps) {
  // Show specific section content if selected
  if (selectedSection && selectedSection.startsWith('cfr-')) {
    const sectionNum = selectedSection.replace('cfr-', '');
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl text-gray-900 mb-1">
            37 CFR §{sectionNum}{sectionContent?.error ? ' not found' : ''}
          </h1>
          <div className="text-sm text-gray-600">Code of Federal Regulations</div>
        </div>
        <div className="mb-6">
          {sectionContent ? (
            <div className="prose max-w-none" style={getFontSizeStyle?.()}>
              {sectionContent.error ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="text-6xl mb-4">📖</div>
                  <h3 className="text-lg font-semibold text-gray-700">Section not found</h3>
                </div>
              ) : sectionContent.cfr_xml_content ? (
                <CfrXmlRenderer xmlString={sectionContent.cfr_xml_content} sectionNumber={sectionNum} />
              ) : sectionContent.content ? (
                <div className="mpep-content" dangerouslySetInnerHTML={{ __html: sectionContent.content }} />
              ) : typeof sectionContent === 'string' ? (
                <div className="mpep-content" dangerouslySetInnerHTML={{ __html: sectionContent }} />
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="text-6xl mb-4">❓</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Unexpected Content Format</h3>
                  <p className="text-gray-600">
                    The section content could not be displayed properly.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-700 leading-relaxed" style={getFontSizeStyle?.()}>
              Loading section content...
            </p>
          )}
        </div>
      </div>
    );
  }

  // Default overview content
  return (
    <div style={getFontSizeStyle?.()}>
      <div className="mb-6">
        <h1 className="text-2xl text-gray-900 mb-1">37 C.F.R. - Code of Federal Regulations</h1>
        <div className="text-gray-600">Title 37 - Patents, Trademarks, and Copyrights</div>
      </div>

      <div className="mb-8 border-l-4 border-purple-600 pl-4">
        <h2 className="text-lg text-gray-900 mb-3">About 37 C.F.R.</h2>
        <p className="text-gray-700 leading-relaxed mb-3">
          Title 37 of the Code of Federal Regulations contains the regulations implementing the patent laws. These rules have the force of law and govern the practice before the USPTO. Part 1 contains the general rules of practice in patent cases, while other parts cover specific proceedings and requirements.
        </p>
        <p className="text-gray-700 leading-relaxed">
          The regulations in 37 C.F.R. provide detailed procedural requirements for patent applications, including forms, fees, time periods, and other administrative matters. Compliance with these regulations is mandatory for obtaining and maintaining patent rights.
        </p>
      </div>

      <div>
        <h2 className="text-lg text-gray-900 mb-4 border-l-4 border-purple-600 pl-4">Key Sections</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <button
            onClick={() => onSectionSelect?.('cfr-1.16')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-purple-50 text-purple-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">1.16</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                  National Application Fees
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Filing, search, and examination fees
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('cfr-1.53')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-purple-50 text-purple-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">1.53</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                  Application Number
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Provisional and nonprovisional applications
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('cfr-1.56')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-purple-50 text-purple-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">1.56</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                  Duty of Disclosure
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Duty to disclose material information (duty of candor)
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('cfr-1.97')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-purple-50 text-purple-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">1.97</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                  Filing an IDS
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Requirements for filing Information Disclosure Statement
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('cfr-1.98')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-purple-50 text-purple-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">1.98</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                  IDS Content
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Content of Information Disclosure Statement
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('cfr-1.105')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-purple-50 text-purple-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">1.105</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                  Requirements for Information
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Examiner requirements for information from applicant
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('cfr-1.111')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-purple-50 text-purple-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">1.111</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                  Reply to Office Action
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Applicant's reply to non-final Office action
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('cfr-1.113')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-purple-50 text-purple-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">1.113</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                  Final Rejections
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Responding to final Office actions
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('cfr-1.114')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-purple-50 text-purple-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">1.114</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                  Request for Continued Examination
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              RCE requirements and procedures
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('cfr-1.136')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-purple-50 text-purple-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">1.136</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                  Extensions of Time
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Extensions of time for replying to Office actions
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('cfr-1.76')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-purple-50 text-purple-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">1.76</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                  Application Data Sheet
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              ADS requirements and bibliographic data
            </p>
          </button>

          <button
            onClick={() => onSectionSelect?.('cfr-1.78')}
            className="text-left p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all bg-white group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-purple-50 text-purple-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-semibold">1.78</span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                  Claiming Benefit
                </div>
              </div>
            </div>
            <p className="text-gray-600">
              Claiming priority and benefit requirements
            </p>
          </button>
        </div>
      </div>
    </div>
  );
}

interface UscContentProps extends ContentProps {
  uscInfo?: any;
  onSectionSelect?: (section: string) => void;
}

function UscContent({ selectedSection, sectionContent, uscInfo, onSectionSelect, getFontSizeStyle }: UscContentProps) {
  // Show specific section content if selected
  if (selectedSection && selectedSection.startsWith('usc-')) {
    const sectionNum = selectedSection.replace('usc-', '');

    // Look up the section from uscInfo
    const sectionData = uscInfo?.usc_info?.[sectionNum];

    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl text-gray-900 mb-1">35 USC §{sectionNum}</h1>
          <div className="text-sm text-gray-600">United States Code - Title 35</div>
        </div>
        <div className="mb-6">
          {sectionData ? (
            <div className="mpep-content prose max-w-none" style={getFontSizeStyle?.()}>
              {/* Display the section header */}
              {sectionData.header && (
                <div className="text-lg font-semibold mb-4" dangerouslySetInnerHTML={{ __html: sectionData.header }} />
              )}
              {/* Display the section content */}
              {sectionData.content && (
                <div dangerouslySetInnerHTML={{ __html: sectionData.content }} />
              )}
              {/* Fallback to raw HTML if content is missing */}
              {!sectionData.content && sectionData.raw_html && (
                <div dangerouslySetInnerHTML={{ __html: sectionData.raw_html }} />
              )}
            </div>
          ) : uscInfo?.usc_info ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-gray-700 leading-relaxed" style={getFontSizeStyle?.()}>
                Section {sectionNum} not found in the database. Please verify the section number.
              </p>
            </div>
          ) : (
            <p className="text-gray-700 leading-relaxed" style={getFontSizeStyle?.()}>
              Loading USC sections...
            </p>
          )}
        </div>
      </div>
    );
  }

  const notableSections = [
    {
      section: '§101',
      category: 'Patentability',
      title: 'Inventions patentable',
      description: 'Subject matter eligibility - what can be patented',
    },
    {
      section: '§102',
      category: 'Novelty',
      title: 'Conditions for patentability; novelty',
      description: 'Novelty requirements and prior art provisions',
    },
    {
      section: '§103',
      category: 'Obviousness',
      title: 'Conditions for patentability; non-obvious subject matter',
      description: 'Obviousness standard for patentability',
    },
    {
      section: '§112',
      category: 'Specification',
      title: 'Specification',
      description: 'Written description, enablement and claims requirements',
    },
    {
      section: '§119',
      category: 'Priority',
      title: 'Benefit of earlier filing date; right of priority',
      description: 'Foreign priority and provisional applications',
    },
    {
      section: '§120',
      category: 'Priority',
      title: 'Benefit of earlier filing date in the United States',
      description: 'Domestic priority and CIP applications',
    },
    {
      section: '§131',
      category: 'Examination',
      title: 'Examination of application',
      description: 'Examiner\'s duty to examine applications',
    },
    {
      section: '§132',
      category: 'Prosecution',
      title: 'Notice of rejection; reexamination',
      description: 'Office action requirements and applicant responses',
    },
    {
      section: '§271',
      category: 'Infringement',
      title: 'Infringement of patent',
      description: 'Direct, induced and contributory infringement',
    },
  ];

  return (
    <div style={getFontSizeStyle?.()}>
      <div className="mb-6">
        <h1 className="text-2xl text-gray-900 mb-1">35 U.S.C. - United States Code</h1>
        <div className="text-gray-600">Title 35 - Patents</div>
      </div>

      <div className="mb-8 border-l-4 border-teal-600 pl-4">
        <h2 className="text-lg text-gray-900 mb-3">About 35 U.S.C.</h2>
        <p className="text-gray-700 leading-relaxed mb-3">
          Title 35 of the United States Code contains the federal statutes governing patent law in the United States. This body of law establishes the legal framework for obtaining and enforcing patent rights, defining what inventions are patentable and outlining the rights and obligations of patent owners.
        </p>
        <p className="text-gray-700 leading-relaxed">
          The statute is organized into five main parts: covering the USPTO, patentability requirements, patent rights and enforcement, the Patent Cooperation Treaty, and the Hague Agreement. These provisions form the statutory foundation that is implemented through regulations at 37 C.F.R. and interpreted through the MPEP.
        </p>
      </div>

      <div>
        <h2 className="text-lg text-gray-900 mb-4 border-l-4 border-teal-600 pl-4">Notable Sections</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {notableSections.map((section, index) => {
            // Extract section number from "§101" format
            const sectionNum = section.section.replace('§', '');
            return (
              <button
                key={index}
                onClick={() => onSectionSelect?.(`usc-${sectionNum}`)}
                className="text-left p-4 border border-gray-200 rounded-lg hover:border-teal-300 hover:shadow-md transition-all bg-white group"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-teal-50 text-teal-700 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span>{section.section}</span>
                    </div>
                    <div className="flex-1">
                      <div className="text-gray-500 mb-1">{section.category}</div>
                      <div className="text-gray-900 group-hover:text-teal-600 transition-colors">
                        {section.title}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-teal-600 transition-colors" />
                </div>
                <p className="text-gray-600 leading-relaxed">
                  {section.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FedRegContent({ selectedSection, fedRegInfo, onSectionSelect, getFontSizeStyle }: ContentProps & { fedRegInfo?: any }) {
  const [copiedCitation, setCopiedCitation] = React.useState<string | null>(null);
  const [fullContent, setFullContent] = React.useState<string | null>(null);
  const [loadingContent, setLoadingContent] = React.useState(false);

  const copyCitation = (citation: string, type: string) => {
    navigator.clipboard.writeText(citation);
    setCopiedCitation(type);
    setTimeout(() => setCopiedCitation(null), 2000);
  };

  // Extract notice data if a specific notice is selected (must be done before hooks)
  let notice: any = null;
  if (selectedSection && selectedSection.startsWith('fedreg-') && fedRegInfo) {
    const noticeId = selectedSection.replace('fedreg-', '');
    const actualData = fedRegInfo.fed_reg_info || fedRegInfo;

    let entriesArray: any[] = [];
    if (Array.isArray(actualData)) {
      entriesArray = actualData;
    } else if (typeof actualData === 'object') {
      entriesArray = Object.entries(actualData).map(([key, value]: [string, any]) => {
        return { ...value, id: value.id || key };
      });
    }

    notice = entriesArray.find((entry: any) => entry.id === noticeId);
  }

  // Fetch full content from S3 when notice is selected - MUST be at top level
  React.useEffect(() => {
    const fetchContent = async () => {
      if (notice && notice.s3_key) {
        setLoadingContent(true);
        try {
          const content = await resourceApi.fetchFedRegContent(notice.id);
          if (content && content.html_content) {
            setFullContent(content.html_content);
          } else if (content && content.content) {
            setFullContent(content.content);
          }
        } catch (error) {
          console.error('Failed to fetch Federal Register content:', error);
        } finally {
          setLoadingContent(false);
        }
      } else {
        // Reset content when no notice is selected
        setFullContent(null);
        setLoadingContent(false);
      }
    };
    fetchContent();
  }, [notice?.id]);

  // If a specific notice is selected
  if (selectedSection && selectedSection.startsWith('fedreg-') && fedRegInfo && notice) {
    // Extract year from id
    const yearMatch = notice.id?.match(/^fed_reg_uspto_(\d{4})\//);
    const publicationYear = yearMatch ? yearMatch[1] : '';

    // Use timestamp for full date display
    const displayDate = notice.timestamp
      ? new Date(notice.timestamp).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      : publicationYear;

    // Generate citations
    const shortCitation = notice.citation?.volume && notice.citation?.pages
      ? `${notice.citation.volume} FR ${notice.citation.pages}`
      : '';

    const longCitation = shortCitation && displayDate
      ? `${shortCitation}, ${displayDate}`
      : '';

    // Generate external links (use link from data if available)
    const federalRegisterUrl = notice.link || notice.federal_register_url || '';
    const govInfoUrl = notice.govinfo_url || '';

    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl text-gray-900 mb-3 leading-tight">{notice.title}</h1>
            <div className="flex items-center gap-4 text-gray-600">
              {displayDate && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {publicationYear || displayDate}
                </div>
              )}
              {shortCitation && (
                <div className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  {shortCitation}
                </div>
              )}
            </div>
          </div>

          {/* Citations Section */}
          {(shortCitation || longCitation) && (
            <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h2 className="font-semibold text-gray-900 mb-3">Citations</h2>

              {shortCitation && (
                <div className="mb-3">
                  <div className="text-gray-600 mb-1">Short Citation</div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border border-orange-200">
                      {shortCitation}
                    </code>
                    <Button
                      onClick={() => copyCitation(shortCitation, 'short')}
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                    >
                      {copiedCitation === 'short' ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {longCitation && (
                <div>
                  <div className="text-gray-600 mb-1">Long Citation</div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border border-orange-200">
                      {longCitation}
                    </code>
                    <Button
                      onClick={() => copyCitation(longCitation, 'long')}
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                    >
                      {copiedCitation === 'long' ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* External Links */}
          {(federalRegisterUrl || govInfoUrl) && (
            <div className="mb-6 flex gap-3">
              {federalRegisterUrl && (
                <a
                  href={federalRegisterUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-50 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  View on FederalRegister.gov
                </a>
              )}
              {govInfoUrl && (
                <a
                  href={govInfoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-50 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  View on GovInfo.gov
                </a>
              )}
            </div>
          )}

          {/* Summary Section */}
          {notice.summary && (
            <div className="mb-6 border-l-4 border-orange-600 pl-4">
              <h2 className="text-lg text-gray-900 mb-3">Summary</h2>
              <div className="text-gray-700 leading-relaxed whitespace-pre-wrap" style={getFontSizeStyle?.()}>
                {notice.summary}
              </div>
            </div>
          )}

          {/* Full Text Content */}
          <div className="border-t border-gray-200 pt-6">
            <h2 className="text-lg text-gray-900 mb-4">Full Text</h2>
            {loadingContent ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-gray-500" style={getFontSizeStyle?.()}>Loading full text from Federal Register...</div>
              </div>
            ) : fullContent ? (
              <div
                className="text-gray-700 leading-relaxed prose max-w-none"
                style={getFontSizeStyle?.()}
                dangerouslySetInnerHTML={{ __html: fullContent }}
              />
            ) : notice.content ? (
              <div
                className="text-gray-700 leading-relaxed prose max-w-none"
                style={getFontSizeStyle?.()}
                dangerouslySetInnerHTML={{ __html: notice.content }}
              />
            ) : (
              <div className="text-gray-500 italic" style={getFontSizeStyle?.()}>
                Full text content not available. Please visit the external links above to view the complete notice.
              </div>
            )}
          </div>
        </div>
      );
    }

  // Overview page - show grouped notices by year
  if (fedRegInfo) {
    // Extract the actual entries from fed_reg_info property
    const actualData = fedRegInfo.fed_reg_info || fedRegInfo;

    // Convert to array if it's an object, preserving IDs
    let entriesArray: any[] = [];
    if (Array.isArray(actualData)) {
      entriesArray = actualData;
    } else if (typeof actualData === 'object') {
      entriesArray = Object.entries(actualData).map(([key, value]: [string, any]) => {
        return { ...value, id: value.id || key };
      });
    }

    // Sort entries by year extracted from id
    const sortedEntries = entriesArray.sort((a: any, b: any) => {
      const matchA = a.id?.match(/^fed_reg_uspto_(\d{4})\//);
      const matchB = b.id?.match(/^fed_reg_uspto_(\d{4})\//);
      const yearA = matchA ? parseInt(matchA[1]) : 0;
      const yearB = matchB ? parseInt(matchB[1]) : 0;

      if (yearA !== yearB) {
        return yearB - yearA;
      }

      const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return timeB - timeA;
    });

    // Group by year
    const groupedByYear = sortedEntries.reduce((acc: any, entry: any) => {
      const match = entry.id?.match(/^fed_reg_uspto_(\d{4})\//);
      const year = match ? match[1] : 'Unknown';
      if (!acc[year]) acc[year] = [];
      acc[year].push(entry);
      return acc;
    }, {});

    return (
      <div style={getFontSizeStyle?.()}>
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Federal Register – USPTO Updates</h1>
          <div className="text-gray-600 italic">
            Source:{" "}
            <a
              href="https://www.federalregister.gov/"
              target="_blank"
              rel="noopener noreferrer"
              className="not-italic hover:underline inline-flex items-center gap-1 text-orange-700"
            >
              federalregister.gov <ExternalLink className="w-4 h-4 opacity-80 inline" />
            </a>
          </div>
        </div>

        <div className="mb-8 border-l-4 border-orange-600 pl-4">
          <h2 className="text-lg text-gray-900 mb-3">About Federal Register</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            The Federal Register is the daily journal of the U.S. Government containing proposed rules, final rules, public notices, and executive orders. The USPTO publishes notices of proposed and final rulemaking, as well as other significant notices affecting patent practice.
          </p>
          <p className="text-gray-700 leading-relaxed">
            These notices provide important information about changes to patent rules and procedures, fee schedules, and other matters of interest to patent practitioners and applicants.
          </p>
        </div>

        {/* Notices grouped by year */}
        <div className="space-y-8">
          {Object.entries(groupedByYear)
            .sort(([a], [b]) => parseInt(b) - parseInt(a))
            .map(([year, entries]: [string, any]) => (
              <div key={year} className="mb-6 border-b pb-4">
                <div className="text-2xl font-semibold mb-2 text-gray-900">
                  {year} ({entries.length} update{entries.length !== 1 ? 's' : ''})
                </div>
                <div className="space-y-4">
                  {entries.map((notice: any) => {
                    // Citation generation matching patentloop-frontend
                    const volume = notice.volume;
                    const pages = notice.pages;
                    const fallbackCitation = notice.citation?.includes("FR")
                      ? notice.citation
                      : notice.citation?.includes("-")
                        ? `${notice.citation.split("-")[0]} FR ${notice.citation.split("-")[1]}`
                        : notice.id?.split("/").pop()?.replace("-", " FR ") || "";
                    const shortCitation = (volume && pages)
                      ? `${volume} FR ${pages}`
                      : fallbackCitation;
                    const longCitation = shortCitation
                      ? `${shortCitation.replace("FR", "Fed. Reg.")} (${new Date(notice.timestamp || notice.date).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })})`
                      : "";

                    // Extract year from id for display
                    const yearMatch = notice.id?.match(/^fed_reg_uspto_(\d{4})\//);
                    const publicationYear = yearMatch ? yearMatch[1] : '';

                    return (
                      <div
                        key={notice.id}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-orange-300 transition-all bg-white"
                      >
                        {/* Title as link */}
                        {notice.link ? (
                          <a
                            href={notice.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-lg font-semibold mb-1 text-orange-700 hover:underline"
                          >
                            {notice.title}
                          </a>
                        ) : (
                          <h3 className="text-lg font-semibold mb-1 text-gray-900">
                            {notice.title}
                          </h3>
                        )}

                        {/* Link URL */}
                        {notice.link && (
                          <div className="break-all flex items-center gap-1 text-blue-600 hover:underline mb-2">
                            <ExternalLink className="inline-block opacity-70 w-3 h-3" />
                            <a href={notice.link} target="_blank" rel="noopener noreferrer">
                              {notice.link}
                            </a>
                          </div>
                        )}

                        {/* Published date */}
                        <div className="text-gray-500 mb-2">
                          Published: {notice.timestamp
                            ? new Date(notice.timestamp).toLocaleDateString()
                            : publicationYear}
                        </div>

                        {/* Citation with copy buttons */}
                        {shortCitation && (
                          <div className="mt-1 flex items-center gap-2 text-orange-700 mb-2">
                            <span>Citation: {shortCitation}</span>
                            <button
                              title="Copy short citation"
                              onClick={() => copyCitation(shortCitation, `short-${notice.id}`)}
                              className="hover:text-orange-900 transition"
                            >
                              {copiedCitation === `short-${notice.id}` ? (
                                <>
                                  <Check className="inline w-4 h-4" />
                                  <span className="ml-1 font-semibold text-green-600">Copied!</span>
                                </>
                              ) : (
                                <Copy className="inline w-4 h-4" />
                              )}
                            </button>
                            {longCitation && (
                              <button
                                title="Copy long citation"
                                onClick={() => copyCitation(longCitation, `long-${notice.id}`)}
                                className="hover:text-orange-900 transition"
                              >
                                {copiedCitation === `long-${notice.id}` ? (
                                  <>
                                    <Check className="inline w-4 h-4" />
                                    <span className="ml-1 font-semibold text-green-600">Copied!</span>
                                  </>
                                ) : (
                                  <Copy className="inline w-4 h-4" />
                                )}
                              </button>
                            )}
                          </div>
                        )}

                        {/* Summary */}
                        {notice.summary && (
                          <div className="mt-3 p-3 rounded bg-gray-50 text-gray-700">
                            <div className="font-medium mb-1">Summary:</div>
                            <div className="leading-relaxed">{notice.summary}</div>
                          </div>
                        )}

                        {/* GovInfo link */}
                        {volume && pages && (
                          <div className="break-all flex items-center gap-1 mt-2 text-blue-600 hover:underline">
                            <ExternalLink className="inline-block opacity-70 w-3 h-3" />
                            <a
                              href={`https://www.govinfo.gov/link/fr/${volume}/${pages.split("-")[0]}?link-type=pdf`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View on GovInfo.gov
                            </a>
                          </div>
                        )}

                        {/* Open Full Text Button */}
                        <Button
                          onClick={() => onSectionSelect(`fedreg-${notice.id}`)}
                          variant="default"
                          size="sm"
                          className="mt-3 w-full bg-orange-600 hover:bg-orange-700 text-white"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Open Full Text
                        </Button>

                        {/* ID and timestamp */}
                        <div className="italic mt-2 text-gray-400">
                          <span className="font-mono">{notice.id}, </span>
                          {notice.timestamp && new Date(notice.timestamp).toLocaleString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
        </div>
      </div>
    );
  }

  // No data available - show placeholder
  return (
    <div style={getFontSizeStyle?.()}>
      <div className="mb-6">
        <h1 className="text-2xl text-gray-900 mb-1">Federal Register Notices</h1>
        <div className="text-gray-600">USPTO Rules and Notices</div>
      </div>

      <div className="mb-8 border-l-4 border-orange-600 pl-4">
        <h2 className="text-lg text-gray-900 mb-3">About Federal Register</h2>
        <p className="text-gray-700 leading-relaxed mb-3">
          The Federal Register is the daily journal of the U.S. Government containing proposed rules, final rules, public notices, and executive orders. The USPTO publishes notices of proposed and final rulemaking, as well as other significant notices affecting patent practice.
        </p>
        <p className="text-gray-700 leading-relaxed">
          These notices provide important information about changes to patent rules and procedures, fee schedules, and other matters of interest to patent practitioners and applicants.
        </p>
      </div>

      <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
        <div className="text-gray-600">Loading Federal Register notices...</div>
      </div>
    </div>
  );
}

function FeesContent({ selectedSection, feeInfo, getFontSizeStyle, onSectionSelect }: ContentProps & { feeInfo?: any }) {
  const [searchQuery, setSearchQuery] = useState('');

  // Show Fee Calculator if selected
  if (selectedSection === 'fee-calculator') {
    return <FeeCalculator feeInfo={feeInfo} getFontSizeStyle={getFontSizeStyle} />;
  }

  // Filter fees based on search query
  const filterFees = (fees: any[], query: string) => {
    if (!query.trim()) return fees;
    const lowerQuery = query.toLowerCase();
    return fees.filter((fee: any) => {
      // Search across all fee fields
      return Object.values(fee).some((value: any) =>
        String(value).toLowerCase().includes(lowerQuery)
      );
    });
  };

  // Filter categories based on search query
  const filterCategories = (categories: any[], query: string) => {
    if (!query.trim()) return categories;
    const lowerQuery = query.toLowerCase();
    return categories
      .map((cat: any) => ({
        ...cat,
        fees: filterFees(cat.fees || [], query)
      }))
      .filter((cat: any) =>
        // Include category if name matches OR it has matching fees
        cat.category?.toLowerCase().includes(lowerQuery) ||
        cat.general_category?.toLowerCase().includes(lowerQuery) ||
        (cat.fees && cat.fees.length > 0)
      );
  };

  // If we have feeInfo data from API, display it
  if (feeInfo && Array.isArray(feeInfo) && feeInfo.length > 0) {
    // If a specific category is selected, show only that category
    if (selectedSection && selectedSection.startsWith('fee-')) {
      const categoryIndex = parseInt(selectedSection.replace('fee-', ''));
      const categoryData = feeInfo[categoryIndex];

      if (categoryData) {
        const filteredFees = filterFees(categoryData.fees || [], searchQuery);

        return (
          <div style={getFontSizeStyle?.()}>
            <div className="mb-6 flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-2xl text-gray-900 mb-1">{categoryData.category}</h1>
                <div className="text-gray-600">{categoryData.general_category || 'USPTO Fee Schedule'}</div>
              </div>
              <Button
                onClick={() => onSectionSelect?.('fee-calculator')}
                className="bg-green-600 hover:bg-green-700 text-white mt-5"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Fee Calculator
              </Button>
            </div>

            {/* Search Input */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search fees..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {filteredFees && filteredFees.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {Object.keys(filteredFees[0] || {}).map((header: string, idx: number) =>
                        header !== "Back to top" ? (
                          <th key={idx} className="text-left p-3 text-gray-700 border">
                            {header.replace(/_/g, " ")}
                          </th>
                        ) : null
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFees.map((fee: any, idx: number) => (
                      <tr key={idx} className="border-t border-gray-200 hover:bg-gray-50">
                        {Object.keys(filteredFees[0] || {}).map((key: string, id: number) =>
                          key !== "Back to top" ? (
                            <td key={id} className="p-3 border">
                              {fee[key] || "-"}
                            </td>
                          ) : null
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : searchQuery ? (
              <div className="text-center py-12 text-gray-500">
                <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No fees found matching "{searchQuery}"</p>
              </div>
            ) : null}
          </div>
        );
      }
    }

    // Default: show all categories
    const filteredCategories = filterCategories(feeInfo, searchQuery);

    return (
      <div style={getFontSizeStyle?.()}>
        <div className="mb-6 flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-2xl text-gray-900 mb-1">USPTO Fee Schedule</h1>
            <div className="text-gray-600">Current Patent Fees</div>
          </div>
          <Button
            onClick={() => onSectionSelect?.('fee-calculator')}
            className="bg-green-600 hover:bg-green-700 text-white mt-5"
          >
            <Calculator className="w-4 h-4 mr-2" />
            Fee Calculator
          </Button>
        </div>

        {/* Search Input */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search all fees..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {filteredCategories.length > 0 ? (
          filteredCategories.map((categoryData: any, index: number) => (
          <div key={index} className="mb-8">
            <div className="text-lg font-semibold p-4 rounded-md shadow-md bg-green-100 text-gray-900 mb-4">
              {categoryData.category}
            </div>

            {categoryData.fees && categoryData.fees.length > 0 && (
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {Object.keys(categoryData.fees[0] || {}).map((header: string, idx: number) =>
                        header !== "Back to top" ? (
                          <th key={idx} className="text-left p-3 text-gray-700 border">
                            {header.replace(/_/g, " ")}
                          </th>
                        ) : null
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {categoryData.fees.map((fee: any, idx: number) => (
                      <tr key={idx} className="border-t border-gray-200 hover:bg-gray-50">
                        {Object.keys(categoryData.fees[0] || {}).map((key: string, id: number) =>
                          key !== "Back to top" ? (
                            <td key={id} className="p-3 border">
                              {fee[key] || "-"}
                            </td>
                          ) : null
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))
        ) : (
          <div className="text-center py-12 text-gray-500">
            <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No fees found matching "{searchQuery}"</p>
          </div>
        )}
      </div>
    );
  }

  // Fallback static content
  const feeCategories = [
    {
      title: 'Basic Filing Fees',
      fees: [
        { name: 'Utility application filing fee', undiscounted: '$320', small: '$160', micro: '$80' },
        { name: 'Design application filing fee', undiscounted: '$220', small: '$110', micro: '$55' },
        { name: 'Plant application filing fee', undiscounted: '$220', small: '$110', micro: '$55' },
      ],
    },
    {
      title: 'Examination Fees',
      fees: [
        { name: 'Utility examination fee', undiscounted: '$800', small: '$400', micro: '$200' },
        { name: 'Design examination fee', undiscounted: '$680', small: '$340', micro: '$170' },
        { name: 'Plant examination fee', undiscounted: '$680', small: '$340', micro: '$170' },
      ],
    },
    {
      title: 'Issue Fees',
      fees: [
        { name: 'Utility issue fee', undiscounted: '$1,200', small: '$600', micro: '$300' },
        { name: 'Design issue fee', undiscounted: '$880', small: '$440', micro: '$220' },
        { name: 'Plant issue fee', undiscounted: '$880', small: '$440', micro: '$220' },
      ],
    },
  ];

  return (
    <div style={getFontSizeStyle?.()}>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl text-gray-900 mb-1">USPTO Fee Schedule</h1>
          <div className="text-gray-600">Current as of January 2025</div>
        </div>
        <Button
          onClick={() => onSectionSelect?.('fee-calculator')}
          className="bg-green-600 hover:bg-green-700 text-white mt-5"
        >
          <Calculator className="w-4 h-4 mr-2" />
          Fee Calculator
        </Button>
      </div>

      <div className="mb-8 border-l-4 border-green-600 pl-4">
        <h2 className="text-lg text-gray-900 mb-3">About USPTO Fees</h2>
        <p className="text-gray-700 leading-relaxed mb-3">
          The USPTO charges fees for filing, searching, examining, issuing, and maintaining patents. Fee amounts vary based on entity size: undiscounted (large entity), small entity (60% discount), and micro entity (80% discount).
        </p>
        <p className="text-gray-700 leading-relaxed">
          Fees are updated periodically to reflect operational costs. Always check the current fee schedule before filing.
        </p>
      </div>

      {feeCategories.map((category, idx) => (
        <div key={idx} className="mb-8">
          <h2 className="text-lg text-gray-900 mb-4 border-l-4 border-green-600 pl-4">{category.title}</h2>
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 text-gray-700">Fee Type</th>
                  <th className="text-right p-3 text-gray-700">Undiscounted</th>
                  <th className="text-right p-3 text-gray-700">Small Entity</th>
                  <th className="text-right p-3 text-gray-700">Micro Entity</th>
                </tr>
              </thead>
              <tbody>
                {category.fees.map((fee, feeIdx) => (
                  <tr key={feeIdx} className="border-t border-gray-200">
                    <td className="p-3 text-gray-900">{fee.name}</td>
                    <td className="p-3 text-right text-gray-900 font-medium">{fee.undiscounted}</td>
                    <td className="p-3 text-right text-gray-900">{fee.small}</td>
                    <td className="p-3 text-right text-gray-900">{fee.micro}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <h3 className="text-gray-900 mb-3">Entity Status</h3>
        <div className="space-y-2 text-gray-700">
          <p><span className="font-medium">Small Entity:</span> Applicants with 500 or fewer employees qualify for 60% fee reduction</p>
          <p><span className="font-medium">Micro Entity:</span> Applicants meeting specific income and filing history requirements qualify for 80% fee reduction</p>
        </div>
      </div>
    </div>
  );
}

function AbstractContent({ selectedSection, getFontSizeStyle }: ContentProps) {
  const [selectedBadge, setSelectedBadge] = useState<string | null>(null);
  const [selectedCase, setSelectedCase] = useState<string | null>(null);

  const handleBadgeClick = (badgeText: string) => {
    setSelectedBadge(badgeText === selectedBadge ? null : badgeText);
  };

  // If a case is selected, show the case detail view
  if (selectedCase && caseDetailsMap[selectedCase]) {
    return (
      <div className="max-w-6xl mx-auto">
        <CaseDisplay
          caseDetail={caseDetailsMap[selectedCase]}
          onBack={() => setSelectedCase(null)}
        />
      </div>
    );
  }

  const usptoResources = [
    { title: 'USPTO Subject Matter Eligibility Homepage', url: 'https://www.uspto.gov/patents/laws/examination-policy/subject-matter-eligibility' },
    { title: 'Artificial Intelligence Strategy (Jan. 2025)', url: 'https://www.uspto.gov/sites/default/files/documents/USPTO-AI_Strategy-Jan_2025.pdf' },
    { title: 'Reminders on evaluating subject matter eligibility of claims under 35 U.S.C. 101 (August 4, 2025)', url: 'https://www.uspto.gov/sites/default/files/documents/memo-101-20250804.pdf' },
    { title: 'The 2024 Guidance Update on Patent Subject Matter Eligibility, Including on Artificial Intelligence (effective July 17, 2024)', url: 'https://www.federalregister.gov/documents/2024/07/17/2024-15377/2024-guidance-update-on-patent-subject-matter-eligibility-including-on-artificial-intelligence' },
    { title: 'Training on the 2024 Guidance Update on Patent Subject Matter Eligibility (posted Dec. 13, 2024)', url: 'https://www.uspto.gov/patents/laws/examination-policy/training-materials-subject-matter-eligibility' },
    { title: '101 Examples 1–36', url: 'https://www.uspto.gov/sites/default/files/documents/101_examples_1to36.pdf' },
    { title: '101 Examples 37–42', url: 'https://www.uspto.gov/sites/default/files/documents/101_examples_37to42_20190107.pdf' },
    { title: '101 Examples 43–46', url: 'https://www.uspto.gov/sites/default/files/documents/peg_oct_2019_app1.pdf' },
    { title: '101 Examples 47–49', url: 'https://www.uspto.gov/sites/default/files/documents/2024-AI-SMEUpdateExamples47-49.pdf' },
    { title: 'FAQs on 2019 PEG', url: 'https://www.uspto.gov/sites/default/files/documents/faqs_on_2019peg_20190107.pdf' },
    { title: 'Form Paragraphs', url: 'https://www.uspto.gov/sites/default/files/documents/form_para_for_2019peg_20190108.pdf' },
  ];

  // Framework Section
  const frameworkSection = (
    <section id="framework" className="mb-12">
      <div className="rounded-xl p-8 bg-white shadow-lg">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-gray-900">
          <span className="w-1 h-8 bg-amber-600 rounded-full"></span>
          §101 Eligibility Framework
        </h2>
        <p className="mb-6 leading-relaxed text-gray-700">
          The USPTO applies a structured two-part test for subject matter eligibility under <strong>35 U.S.C. § 101</strong>,
          derived from Supreme Court and Federal Circuit precedent (e.g., <em>Mayo</em>, <em>Alice</em>, <em>Bilski</em>).
          The framework below is based on the USPTO's 2019 Revised Patent Subject Matter Eligibility Guidance.
        </p>

        <div className="space-y-5">
          {/* Step 1 */}
          <div className="p-5 rounded-lg border-l-4 bg-gradient-to-r from-amber-50 to-transparent border-amber-500">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold bg-amber-600 text-white">
                1
              </div>
              <div className="flex-1">
                <div className="font-bold mb-1 text-gray-900">
                  Step 1: <span className="underline decoration-2">Statutory Category</span>
                </div>
                <p className="mb-2 text-gray-700">
                  Is the claim directed to one of the four categories?
                </p>
                <p className="italic text-gray-600">
                  (Process, Machine, Manufacture, or Composition of Matter)
                </p>
              </div>
            </div>
          </div>

          {/* Step 2 Container */}
          <div className="p-5 rounded-lg border-l-4 bg-gradient-to-r from-purple-50 to-transparent border-purple-500">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold bg-purple-600 text-white">
                2
              </div>
              <div className="flex-1">
                <div className="font-bold text-base mb-1 text-gray-900">
                  Step 2: Alice/Mayo Test
                </div>
              </div>
            </div>

            {/* Step 2A Subparts */}
            <div className="ml-11 space-y-4">
              {/* Step 2A - Prong One */}
              <div className="p-4 rounded-lg border bg-white border-purple-200">
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-bold bg-purple-500 text-white">
                    2A
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold mb-1 text-gray-900">
                      Prong One: <span className="underline decoration-2">Judicial Exception?</span>
                    </div>
                    <p className="mb-2 text-gray-700">
                      Is the claim directed to a judicial exception (i.e., a law of nature, natural phenomenon, or abstract idea)?
                    </p>
                    <p className="italic text-gray-600">
                      Abstract ideas include: mathematical concepts, methods of organizing human activity, and mental processes.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 2A - Prong Two */}
              <div className="p-4 rounded-lg border bg-white border-purple-200">
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-bold bg-purple-500 text-white">
                    2A
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold mb-1 text-gray-900">
                      Prong Two: <span className="underline decoration-2">Practical Application?</span>
                    </div>
                    <p className="mb-2 text-gray-700">
                      Does the claim integrate the exception into a practical application?
                    </p>
                    <p className="italic text-gray-600">
                      Consider whether the claim applies the exception in a meaningful way beyond simply stating it.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 2B */}
              <div className="p-4 rounded-lg border bg-white border-purple-200">
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-bold bg-purple-500 text-white">
                    2B
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold mb-1 text-gray-900">
                      <span className="underline decoration-2">Inventive Concept?</span>
                    </div>
                    <p className="mb-2 text-gray-700">
                      Does the claim add significantly more to the exception?
                    </p>
                    <p className="italic text-gray-600">
                      Look for an unconventional element or combination that amounts to an inventive concept (i.e., beyond routine, conventional activity).
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  // Resources Section
  const resourcesSection = (
    <section id="resources" className="mb-12">
      <div className="rounded-xl p-8 bg-white shadow-lg">
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-gray-900">
          <span className="w-1 h-8 bg-green-600 rounded-full"></span>
          USPTO Resources & Examples
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {usptoResources.map((doc, idx) => (
            <a
              key={idx}
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 rounded-lg border transition-all group bg-gray-50 border-gray-200 hover:bg-amber-50 hover:border-amber-300 hover:shadow-md"
            >
              <FileText className="w-5 h-5 flex-shrink-0 text-amber-600" />
              <span className="font-medium group-hover:underline text-gray-700">
                {doc.title}
              </span>
              <ExternalLink className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-amber-600" />
            </a>
          ))}
        </div>
      </div>
    </section>
  );

  // Precedents Section
  const precedentsSection = (
    <section id="precedents">
      <div className="rounded-xl p-8 bg-white shadow-lg">
        <h2 className="text-2xl font-bold mb-2 flex items-center gap-3 text-gray-900">
          <span className="w-1 h-8 bg-purple-600 rounded-full"></span>
          Important Court Precedents
        </h2>

        {selectedBadge && (
          <div className="mb-4 px-4 py-2 rounded-lg border-l-4 border-amber-500 bg-amber-50">
            <p>
              <strong>Filtered by:</strong> <span className="font-mono">{selectedBadge}</span>
              <button
                onClick={() => setSelectedBadge(null)}
                className="ml-3 underline hover:no-underline"
              >
                Clear filter
              </button>
            </p>
          </div>
        )}

        <div className="mt-6">
          {courtPrecedents.map((group, idx) => (
            <div key={idx} className="mb-8">
              <h3 className="font-bold text-lg mb-4 pb-2 border-b text-gray-900 border-gray-200">
                {group.category}
              </h3>
              <div className="space-y-4">
                {group.cases
                  .filter(c => {
                    if (!selectedBadge) return true;
                    const normalizeText = (text: string) => text.replace(/\s+/g, ' ').trim();
                    const normalizedSelectedBadge = normalizeText(selectedBadge);
                    // Filter by badges
                    return c.detail?.badges?.some(b => normalizeText(b.text) === normalizedSelectedBadge);
                  })
                  .map((c, i) => (
                    <div key={i} className="p-4 rounded-lg border transition-all bg-gray-50 border-gray-200 hover:border-amber-300 hover:shadow">
                      <div className="flex flex-col gap-3">
                        <div>
                          <div className="font-semibold mb-1 text-gray-900">
                            {c.title}
                          </div>
                          <p className="mb-3 text-gray-700">
                            {c.description}
                          </p>

                          {/* Badges */}
                          {c.detail?.badges && c.detail.badges.length > 0 && (
                            <div>
                              <div className="flex gap-2 flex-wrap">
                                {c.detail.badges.map((badge, bIdx) => (
                                  <button
                                    key={bIdx}
                                    onClick={() => handleBadgeClick(badge.text)}
                                    className={`px-2.5 py-1 rounded-full font-medium border transition-all
                                      ${badge.type === 'ineligible' ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' : ''}
                                      ${badge.type === 'eligible' ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' : ''}
                                      ${badge.type === 'warning' ? 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100' : ''}
                                      ${badge.type === 'guidance' ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100' : ''}
                                      ${badge.type === 'innovation' ? 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100' : ''}
                                      ${selectedBadge === badge.text ? 'ring-2 ring-offset-1 ring-amber-500' : ''}
                                    `}
                                  >
                                    {badge.text}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {caseDetailsMap[c.id] && (
                            <Button
                              onClick={() => setSelectedCase(c.id)}
                              className="bg-amber-600 hover:bg-amber-700 text-white"
                              size="sm"
                            >
                              View Details
                            </Button>
                          )}
                          {c.link && (
                            <a
                              href={c.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-4 py-2 font-medium rounded-lg transition-all bg-gray-200 hover:bg-gray-300 text-gray-700 shadow-sm hover:shadow"
                            >
                              View PDF ↗
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  // Main render
  return (
    <div className="max-w-5xl mx-auto" style={getFontSizeStyle?.()}>
      {/* Header */}
      <div className="mb-10 pb-6 border-b-2 border-gray-200">
        <h1 className="text-4xl font-bold mb-2 text-gray-900">
          Abstract Idea Guide
        </h1>
        <p className="text-lg text-gray-600">
          35 U.S.C. § 101 – Patent Subject Matter Eligibility
        </p>
      </div>

      {/* Render selected section or all sections */}
      {!selectedSection || selectedSection === 'framework' ? frameworkSection : null}
      {!selectedSection || selectedSection === 'resources' ? resourcesSection : null}
      {!selectedSection || selectedSection === 'precedents' ? precedentsSection : null}
    </div>
  );
}

// Search Result Item Component
interface SearchResultItemProps {
  result: any;
  activeResource: ResourceType;
  onSectionSelect: (section: string) => void;
  onClose: () => void;
  fedRegInfo?: any;
}

function SearchResultItem({ result, activeResource, onSectionSelect, onClose, fedRegInfo }: SearchResultItemProps) {
  // Extract section information from result
  const getSectionInfo = () => {
    if (activeResource === 'mpep') {
      return {
        section: result.section_id || result.section || result.id || 'Unknown',
        title: result.section_name || result.subheader || result.title || '',
        content: result.content || result.text || ''
      };
    } else if (activeResource === '37cfr') {
      // For CFR, the title field is often just "37", so skip it and use subgroup instead
      const rawTitle = result.subgroup || result.section_name || result.title || '';
      const cleanTitle = rawTitle === '37' ? '' : rawTitle;
      return {
        section: result.section_id || result.section || result.id || 'Unknown',
        title: cleanTitle,
        content: result.content || result.text || ''
      };
    } else if (activeResource === '35usc') {
      return {
        section: result.section_id || result.section || result.id || 'Unknown',
        title: result.section_name || result.title || '',
        content: result.content || result.text || ''
      };
    } else if (activeResource === 'fedreg') {
      // For Federal Register, use citation or volume/pages instead of section
      let sectionLabel = '';
      if (result.citation) {
        sectionLabel = result.citation;
      } else if (result.volume && result.pages) {
        sectionLabel = `Vol. ${result.volume}, pp. ${result.pages}`;
      } else if (result.date) {
        sectionLabel = new Date(result.date).toLocaleDateString();
      } else {
        sectionLabel = result.id || 'Unknown';
      }

      return {
        section: sectionLabel,
        title: result.title || result.heading || '',
        content: result.text || result.content || ''
      };
    }
    return { section: 'Unknown', title: '', content: '' };
  };

  const { section, title, content } = getSectionInfo();

  // Helper function to fix spacing around common italicized Latin terms
  const fixLatinTermSpacing = (text: string) => {
    // Common Latin legal terms (escape periods for regex)
    const latinTerms = [
      'ab initio', 'ad hoc', 'bona fide', 'caveat', 'cf\\.', 'de facto', 'de jure', 'de novo',
      'dictum', 'e\\.g\\.', 'et al\\.?', 'ex parte', 'ex officio', 'i\\.e\\.', 'id\\.', 'in re',
      'in toto', 'infra', 'inter alia', 'inter partes', 'ipso facto', 'laches', 'mens rea',
      'mutatis mutandis', 'nunc pro tunc', 'obiter dictum', 'pari passu', 'passim',
      'per annum', 'per curiam', 'per se', 'prima facie', 'pro rata', 'quid pro quo',
      'ratio decidendi', 'res judicata', 'sine qua non', 'stare decisis', 'sub silentio',
      'sua sponte', 'sui generis', 'supra', 'ultra vires'
    ].join('|');

    const pattern = new RegExp(`([a-zA-Z])(${latinTerms})`, 'gi');
    const patternAfter = new RegExp(`(${latinTerms})([a-zA-Z])`, 'gi');

    return text
      // Add space before Latin terms if missing
      .replace(pattern, '$1 $2')
      // Add space after Latin terms if missing
      .replace(patternAfter, '$1 $2');
  };

  // Strip HTML tags from title while preserving spaces
  const cleanTitle = title ? fixLatinTermSpacing(title.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()) : '';

  const handleClick = () => {
    // Navigate to the section
    if (activeResource === 'mpep') {
      onSectionSelect(`mpep-${section}`);
    } else if (activeResource === '37cfr') {
      onSectionSelect(`cfr-${section}`);
    } else if (activeResource === '35usc') {
      onSectionSelect(`usc-${section}`);
    } else if (activeResource === 'fedreg') {
      // For Federal Register, look up the entry in fedRegInfo to get the correct ID
      if (fedRegInfo) {
        const actualData = fedRegInfo.fed_reg_info || fedRegInfo;

        let entriesArray: any[] = [];
        if (Array.isArray(actualData)) {
          entriesArray = actualData;
        } else if (typeof actualData === 'object') {
          entriesArray = Object.entries(actualData).map(([key, value]: [string, any]) => {
            return { ...value, id: value.id || key };
          });
        }

        // Try to find the matching entry by ID first, then by citation or title
        let matchedEntry = entriesArray.find((entry: any) =>
          entry.id === result.id ||
          (result.citation && entry.citation?.volume === result.citation?.volume &&
           entry.citation?.pages === result.citation?.pages) ||
          (result.title && entry.title === result.title)
        );

        if (matchedEntry && matchedEntry.id) {
          onSectionSelect(`fedreg-${matchedEntry.id}`);
        }
      }
    }
    onClose();
  };

  // Truncate content for preview
  const truncateContent = (text: string, maxLength: number = 200) => {
    if (!text) return '';
    // Replace HTML tags with spaces to preserve word boundaries, then fix Latin term spacing
    const stripped = fixLatinTermSpacing(text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim());
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength) + '...';
  };

  return (
    <button
      onClick={handleClick}
      className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
    >
      <div className="flex items-start gap-3">
        <ChevronRight className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="font-mono">
              {activeResource === 'mpep' && 'MPEP'}
              {activeResource === '37cfr' && '37 CFR'}
              {activeResource === '35usc' && '35 USC §'}
              {activeResource === 'fedreg' && 'Fed Reg'}
              {' '}{section}
            </Badge>
            {cleanTitle && (
              <span className="font-medium text-gray-900 truncate">
                {cleanTitle}
              </span>
            )}
          </div>
          <p className="text-gray-600 line-clamp-2">
            {truncateContent(content)}
          </p>
        </div>
      </div>
    </button>
  );
}
